/**
 * StaticPages.jsx — /about /support /status /pricing /contact /sales /privacy /terms /sla /legal
 *
 * Tüm statik sayfalar tek dosyada, yönetilmesi kolay.
 * Her sayfa kendi route'undan ayrı ayrı export edilir.
 *
 * KULLANICIYA GÖRÜNEN TÜM METİNLER i18n üzerinden gelir.
 * i18n anahtar yolu: public.static.static-pages.[sayfa]-page.*
 *   örn: public.static.static-pages.about-page.hero_title
 * Ortak anahtarlar: public.static.static-pages.common.*
 * Flat key formatı kullanıldığından i18next config'inde keySeparator: false olmalıdır.
 *
 * JSX içinde sabit kalan veriler yalnızca dil-bağımsız tekniklerdir:
 *  - FontAwesome icon class adları (fa-shield, fa-envelope, ...)
 *  - URL/route'lar
 *  - Ürün/teknoloji marka adları (LDAP, PostgreSQL, Tatalli IAM, ...)
 *  - Sayısal değerler (fiyat, uptime yüzdesi)
 *  - Renk/status enum anahtarları (operational, degraded, outage)
 */
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../../../Layouts/Shared/_PublicLayout';
import apiClient from '../../../Services/apiClient';
/* ─── Design tokens ─── */
const T = {
  navy: '#0a2540',
  blue: '#0050d8',
  blueLt: '#e8f0fe',
  border: '#d9dde3',
  bg: '#f5f7fa',
  muted: '#4a5568',
  light: '#6b7a8d',
  green: '#15803d',
  greenBg: '#f0fdf4',
};

/* ─── ROUTES central ─── */
const ROUTES = {
  home: '/',
  login: '/portal/login',
  pricing: '/pricing',
  calculator: '/calculator',
  contact: '/contact',
  sales: '/sales',
  docs: '/docs',
  about: '/about',
  support: '/support',
  status: '/status',
  infrastructure: '/infrastructure',
  terms: '/terms',
  privacy: '/privacy',
  sla: '/sla',
  pricing_purchase_options: '/pricing/purchase-options',
};

/* ─── Common i18n key ─── */
const COMMON = 'public.static.static-pages.common';

/* ─── Shared components ─── */

function FormInput({ label, type = 'text', placeholder, textarea = false, value, onChange, name }) {
  const style = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${T.border}`,
    borderRadius: 2,
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
    color: T.navy,
    boxSizing: 'border-box',
    background: '#fff',
    resize: textarea ? 'vertical' : undefined,
    minHeight: textarea ? 110 : undefined
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{label}</label>
      {textarea ? (
        <textarea name={name} value={value} onChange={onChange} placeholder={placeholder} style={style} />
      ) : (
        <input name={name} type={type} value={value} onChange={onChange} placeholder={placeholder} style={style} />
      )}
    </div>
  );
}

function SubmitButton({ label, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        padding: '11px 24px',
        background: disabled ? T.muted : T.blue,
        color: '#fff',
        border: 'none',
        borderRadius: 2,
        fontSize: 13,
        fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'inherit',
        transition: 'background .15s'
      }}>
      {label}
    </button>
  );
}

/* Breadcrumb — sayfa adı i18n key'i p.breadcrumb üzerinden gelir */
function Breadcrumb({ label, color }) {
  const { t } = useTranslation();
  const c = color || T.light;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13, color: c }}>
      <Link to={ROUTES.home} style={{ color: c, textDecoration: 'none' }}>{t(`${COMMON}.breadcrumb_home`)}</Link>
      <i className="fas fa-chevron-right" style={{ fontSize: 10 }} />
      <span>{label}</span>
    </div>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: AboutPage — /about
──────────────────────────────────────────────────── */
export function AboutPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.about-page';

  const valueItems = t(`${p}.values`, { returnObjects: true }) || [];
  const values = [
    { icon: 'fa-shield', ...(valueItems[0] || {}) },
    { icon: 'fa-tachometer-alt', ...(valueItems[1] || {}) },
    { icon: 'fa-handshake', ...(valueItems[2] || {}) },
    { icon: 'fa-leaf', ...(valueItems[3] || {}) },
  ];

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .ab-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .ab-govde { padding: 36px 0; }
        .ab-section { margin-bottom: 48px; }
        .ab-section:last-child { margin-bottom: 0; }
        .ab-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; }
        .ab-card { padding: 24px; background: #fff; border: 1px solid ${T.border}; border-radius: 4px; }
        .ab-card-icon { width: 44px; height: 44px; border-radius: 50%; background: ${T.blueLt}; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
        .ab-card-title { font-size: 15px; font-weight: 700; color: ${T.navy}; margin-bottom: 8px; }
        .ab-card-text { font-size: 13px; color: ${T.muted}; line-height: 1.6; margin: 0; }
        .ab-intro { max-width: 720px; margin-bottom: 32px; }
        .ab-intro p { font-size: 16px; color: ${T.muted}; line-height: 1.8; margin-bottom: 16px; }
        @media (max-width: 768px) {
          .ab-hero { padding: 28px 0; }
          .ab-govde { padding: 28px 0; }
          .ab-section { margin-bottom: 36px; }
          .ab-grid { grid-template-columns: repeat(2, 1fr); gap: 16px; }
        }
        @media (max-width: 480px) {
          .ab-hero { padding: 20px 0; }
          .ab-govde { padding: 20px 0; }
          .ab-section { margin-bottom: 28px; }
          .ab-grid { grid-template-columns: 1fr; gap: 12px; }
          .ab-card { padding: 16px; }
        }
      `}</style>

      <div className="ab-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 700, color: T.navy, marginBottom: 12 }}>{t(`${p}.hero_title`)}</h1>
          <p style={{ fontSize: 15, color: T.muted, margin: 0, maxWidth: 520 }}>{t(`${p}.hero_desc`)}</p>
        </div>
      </div>

      <div className="ab-govde">
        <div className="pl-container">
          <div className="ab-section ab-intro">
            <h2 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 16 }}>{t(`${p}.mission_title`)}</h2>
            <p>{t(`${p}.mission_text`)}</p>
          </div>

          <div className="ab-section">
            <h2 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 24 }}>{t(`${p}.values_title`)}</h2>
            <div className="ab-grid">
              {values.map((val, i) => (
                <div key={i} className="ab-card">
                  <div className="ab-card-icon">
                    <i className={`fas ${val.icon}`} style={{ color: T.blue, fontSize: 18 }} />
                  </div>
                  <h3 className="ab-card-title">{val.title}</h3>
                  <p className="ab-card-text">{val.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="ab-section">
            <h2 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 12 }}>{t(`${p}.team_title`)}</h2>
            <p style={{ fontSize: 15, color: T.muted, maxWidth: 720 }}>{t(`${p}.team_text`)}</p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: SupportPage — /support
──────────────────────────────────────────────────── */
export function SupportPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.support-page';
  const [expanded, setExpanded] = useState(null);

  const faqs = t(`${p}.faqs`, { returnObjects: true }) || [];

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .sup-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .sup-govde { padding: 36px 0; }
        .sup-faq-section { margin-bottom: 48px; }
        .sup-faq-item { border: 1px solid ${T.border}; border-radius: 4px; margin-bottom: 12px; overflow: hidden; }
        .sup-faq-header { padding: 16px 20px; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: space-between; transition: background 0.15s; }
        .sup-faq-header:hover { background: ${T.bg}; }
        .sup-faq-header.active { background: ${T.blueLt}; }
        .sup-faq-q { font-size: 14px; font-weight: 600; color: ${T.navy}; flex: 1; }
        .sup-faq-arrow { font-size: 12px; color: ${T.light}; transition: transform 0.2s; }
        .sup-faq-arrow.active { transform: rotate(180deg); }
        .sup-faq-body { padding: 16px 20px; background: #fff; border-top: 1px solid ${T.border}; font-size: 13px; color: ${T.muted}; line-height: 1.6; }
        .sup-contact-box { background: ${T.blueLt}; border: 1px solid ${T.blue}; border-radius: 4px; padding: 24px; max-width: 560px; }
        .sup-contact-title { font-size: 16px; font-weight: 700; color: ${T.navy}; margin-bottom: 8px; }
        .sup-contact-text { font-size: 14px; color: ${T.muted}; margin-bottom: 16px; }
        .sup-contact-btns { display: flex; gap: 12px; flex-wrap: wrap; }
        @media (max-width: 768px) {
          .sup-hero { padding: 28px 0; }
          .sup-govde { padding: 28px 0; }
          .sup-faq-section { margin-bottom: 36px; }
        }
        @media (max-width: 480px) {
          .sup-hero { padding: 20px 0; }
          .sup-govde { padding: 20px 0; }
          .sup-faq-section { margin-bottom: 28px; }
          .sup-faq-header { padding: 14px 16px; }
          .sup-contact-btns { flex-direction: column; }
        }
      `}</style>

      <div className="sup-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 700, color: T.navy, marginBottom: 12 }}>{t(`${p}.hero_title`)}</h1>
          <p style={{ fontSize: 15, color: T.muted, margin: 0, maxWidth: 520 }}>{t(`${p}.hero_desc`)}</p>
        </div>
      </div>

      <div className="sup-govde">
        <div className="pl-container">
          <div className="sup-faq-section">
            <h2 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 24 }}>{t(`${p}.faq_title`)}</h2>
            {faqs.map((faq, i) => (
              <div key={i} className="sup-faq-item">
                <div
                  className={`sup-faq-header${expanded === i ? ' active' : ''}`}
                  onClick={() => setExpanded(expanded === i ? null : i)}
                >
                  <span className="sup-faq-q">{faq.q}</span>
                  <i className={`fas fa-chevron-down sup-faq-arrow${expanded === i ? ' active' : ''}`} />
                </div>
                {expanded === i && <div className="sup-faq-body">{faq.a}</div>}
              </div>
            ))}
          </div>

          <div className="sup-contact-box">
            <h3 className="sup-contact-title">{t(`${p}.contact_title`)}</h3>
            <p className="sup-contact-text">{t(`${p}.contact_text`)}</p>
            <div className="sup-contact-btns">
              <Link to={ROUTES.contact} className="pl-btn pl-btn-primary" style={{ fontSize: 13 }}>
                <i className="fas fa-envelope" style={{ marginRight: 6 }} />{t(`${p}.cta_contact`)}
              </Link>
              <Link to={ROUTES.docs} className="pl-btn pl-btn-ghost" style={{ fontSize: 13 }}>
                <i className="fas fa-book" style={{ marginRight: 6 }} />{t(`${p}.cta_docs`)}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: StatusPage — /status
──────────────────────────────────────────────────── */
export function StatusPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.status-page';

  // Ürün/servis adları marka olduğu için kodda tutulur, status enum değeri kodda tutulur.
  const SERVICES = [
    { name: 'LDAP', status: 'operational', uptime: '100.00%' },
    { name: 'Tatalli IAM', status: 'operational', uptime: '99.99%' },
    { name: 'PostgreSQL', status: 'operational', uptime: '99.99%' },
    { name: 'Artemis MQ', status: 'operational', uptime: '100.00%' },
    { name: 'FTP Storage', status: 'operational', uptime: '99.98%' },
    { name: 'S3 Storage', status: 'operational', uptime: '99.99%' },
    { name: 'Corvia SaaS', status: 'operational', uptime: '99.99%' },
    { name: 'API Gateway', status: 'operational', uptime: '100.00%' },
  ];

  const STATUS_COLORS = { operational: T.green, degraded: '#b45309', outage: '#b91c1c' };
  // status enum → i18n key map
  const statusLabel = (s) => t(`${p}.status_${s}`);

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .st-hero { background: ${T.greenBg}; border-bottom: 1px solid #bbf7d0; padding: 32px 0; }
        .st-hero-content { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
        .st-icon { width: 48px; height: 48px; border-radius: 50%; background: #dcfce7; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .st-btn { padding: 9px 18px; border: 1px solid #86efac; border-radius: 2px; background: #fff; cursor: pointer; font-size: 13px; font-weight: 600; color: ${T.green}; font-family: inherit; white-space: nowrap; margin-left: auto; }
        .st-uptime { padding: 28px 0; border-bottom: 1px solid ${T.border}; }
        .st-uptime-label { display: flex; justify-content: space-between; margin-bottom: 12px; }
        .st-uptime-bar { display: flex; gap: 2px; overflow-x: auto; -webkit-overflow-scrolling: touch; min-height: 28px; }
        .st-uptime-bar::-webkit-scrollbar { height: 3px; }
        .st-uptime-bar::-webkit-scrollbar-track { background: transparent; }
        .st-uptime-bar::-webkit-scrollbar-thumb { background: ${T.light}; border-radius: 2px; }
        .st-uptime-bar > div { flex: 0 0 calc(100% / 30); min-width: 6px; height: 28px; border-radius: 1px; }
        .st-incidents { padding: 24px 0; background: ${T.bg}; border-bottom: 1px solid ${T.border}; }
        .st-box { display: flex; align-items: center; gap: 10px; padding: 14px 16px; background: #fff; border: 1px solid ${T.border}; border-radius: 3px; }
        .st-services { padding: 36px 0; }
        .st-service-item { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid ${T.border}; flex-wrap: wrap; gap: 8px; }
        .st-service-item:last-child { border-bottom: none; }
        .st-service-item:nth-child(even) { background: ${T.bg}; }
        .st-service-name { font-size: 14px; font-weight: 600; color: ${T.navy}; min-width: 200px; }
        .st-uptime-text { font-size: 12px; color: ${T.light}; min-width: 80px; text-align: right; }
        .st-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; border-radius: 2px; padding: 4px 10px; white-space: nowrap; }
        .st-dot { width: 6px; height: 6px; border-radius: 50%; }
        @media (max-width: 768px) {
          .st-hero { padding: 28px 0; }
          .st-btn { margin-left: 0; margin-top: 8px; }
          .st-uptime { padding: 24px 0; }
          .st-incidents { padding: 20px 0; }
          .st-services { padding: 28px 0; }
        }
        @media (max-width: 480px) {
          .st-hero { padding: 20px 0; }
          .st-hero-content { flex-direction: column; align-items: flex-start; }
          .st-btn { margin-left: 0; width: 100%; }
          .st-uptime { padding: 20px 0; }
          .st-incidents { padding: 16px 0; }
          .st-services { padding: 20px 0; }
          .st-service-name { min-width: auto; }
          .st-uptime-text { min-width: auto; }
        }
      `}</style>

      <div className="st-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} color="#166534" />
          <div className="st-hero-content">
            <div className="st-icon"><i className="fas fa-check" style={{ color: T.green, fontSize: 20 }} /></div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.green, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>{t(`${p}.eyebrow`)}</div>
              <h1 style={{ fontSize: 'clamp(20px, 4vw, 28px)', fontWeight: 700, color: '#14532d', margin: '0 0 4px' }}>{t(`${p}.hero_title`)}</h1>
              <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>{t(`${p}.all_operational`)}</p>
            </div>
            <button className="st-btn"><i className="fas fa-bell" style={{ marginRight: 6 }} />{t(`${p}.subscribe`)}</button>
          </div>
        </div>
      </div>

      <div className="st-uptime">
        <div className="pl-container">
          <div className="st-uptime-label">
            <span style={{ fontSize: 12, fontWeight: 600, color: T.light }}>{t(`${p}.uptime_label`)}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.green }}>99.99%</span>
          </div>
          <div className="st-uptime-bar">
            {Array.from({ length: 90 }, (_, i) => (
              <div
                key={i}
                style={{ background: i === 14 || i === 52 ? '#fde68a' : '#4ade80', opacity: i === 14 || i === 52 ? 1 : 0.7 }}
                title={t(`${p}.day_tooltip`, { n: 90 - i })}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="st-incidents">
        <div className="pl-container">
          <div className="st-box">
            <i className="fas fa-info-circle" style={{ color: T.light, fontSize: 14, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{t(`${p}.incident_title`)}</div>
              <div style={{ fontSize: 12, color: T.muted }}>{t(`${p}.incident_desc`)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="st-services">
        <div className="pl-container">
          <h2 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 20 }}>{t(`${p}.services_title`)}</h2>
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 4, overflow: 'hidden' }}>
            {SERVICES.map((svc) => (
              <div key={svc.name} className="st-service-item">
                <span className="st-service-name">{svc.name}</span>
                <span className="st-uptime-text">{svc.uptime}</span>
                <span className="st-badge" style={{ color: STATUS_COLORS[svc.status], background: `${STATUS_COLORS[svc.status]}10`, border: `1px solid ${STATUS_COLORS[svc.status]}25` }}>
                  <span className="st-dot" style={{ background: STATUS_COLORS[svc.status] }} />
                  {statusLabel(svc.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: PricingPage — /pricing
──────────────────────────────────────────────────── */
export function PricingPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.pricing-page';

  // Ürün adları ve fiyatlar dil-bağımsızdır, kodda tutulur.
  const SERVICES = [
    { name: 'LDAP', starter: '$49', pro: '$99', ent: 'custom' },
    { name: 'Tatalli IAM', starter: '$89', pro: '$189', ent: 'custom' },
    { name: 'PostgreSQL', starter: '$69', pro: '$189', ent: '$499' },
    { name: 'Artemis MQ', starter: '$59', pro: '$149', ent: '—' },
    { name: 'FTP Storage', starter: '$29', pro: '$79', ent: '$199' },
    { name: 'S3 Storage', starter: '$39', pro: '$99', ent: '$299' },
    { name: 'Corvia SaaS', starter: '—', pro: '$359', ent: '$559' },
  ];

  const per_month = t(`${p}.per_month`);
  const custom_label = t(`${p}.custom`);
  const renderCell = (val) => {
    if (val === '—') return <>{val}</>;
    if (val === 'custom') return <>{custom_label}</>;
    return <>{val}<span style={{ color: T.light, fontSize: 11 }}>{per_month}</span></>;
  };

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .sp-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .sp-govde { padding: 36px 0; }
        .sp-table-wrapper { border: 1px solid ${T.border}; border-radius: 4px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .sp-table-wrapper table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 500px; }
        .sp-table-wrapper thead tr { background: ${T.bg}; border-bottom: 1px solid ${T.border}; }
        .sp-table-wrapper th { padding: 12px 16px; text-align: left; font-weight: 700; color: ${T.navy}; white-space: nowrap; }
        .sp-table-wrapper th.center { text-align: center; }
        .sp-table-wrapper tbody td { padding: 12px 16px; }
        .sp-table-wrapper tbody tr:nth-child(even) { background: ${T.bg}; }
        .sp-table-wrapper tbody td.center { text-align: center; }
        .sp-table-wrapper tbody td.pro { color: ${T.blue}; font-weight: 600; }
        .sp-table-wrapper tbody td.name { font-weight: 600; color: ${T.navy}; }
        .sp-cta { padding: 48px 0; background: ${T.navy}; text-align: center; }
        .sp-cta h2 { color: #fff; margin: 0 0 10px; font-size: 24px; }
        .sp-cta p { color: rgba(255,255,255,0.5); margin: 0 auto 28px; max-width: 380px; font-size: 14px; }
        .sp-cta-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
        @media (max-width: 768px) {
          .sp-table-wrapper { margin: 0 -12px; }
          .sp-hero { padding: 28px 0; }
          .sp-govde { padding: 28px 0; }
        }
        @media (max-width: 480px) {
          .sp-hero { padding: 20px 0; }
          .sp-govde { padding: 20px 0; }
          .sp-cta { padding: 36px 0; }
        }
      `}</style>

      <div className="sp-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 700, color: T.navy, marginBottom: 12 }}>{t(`${p}.hero_title`)}</h1>
          <p style={{ fontSize: 15, color: T.muted, margin: 0, maxWidth: 520 }}>{t(`${p}.hero_desc`)}</p>
        </div>
      </div>

      <div className="sp-govde">
        <div className="pl-container">
          <h2 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 24 }}>{t(`${p}.plans_title`)}</h2>
          <div className="sp-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>{t(`${p}.col_service`)}</th>
                  <th className="center">{t(`${p}.col_starter`)}</th>
                  <th className="center" style={{ background: T.blueLt, color: T.blue }}>{t(`${p}.col_pro`)}</th>
                  <th className="center">{t(`${p}.col_enterprise`)}</th>
                </tr>
              </thead>
              <tbody>
                {SERVICES.map((svc) => (
                  <tr key={svc.name}>
                    <td className="name">{svc.name}</td>
                    <td className="center">{renderCell(svc.starter)}</td>
                    <td className="center pro">
                      {svc.pro === '—' ? svc.pro : (
                        <>{svc.pro}<span style={{ color: T.light, fontSize: 11, fontWeight: 400 }}>{per_month}</span></>
                      )}
                    </td>
                    <td className="center">{renderCell(svc.ent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="sp-cta">
        <div className="pl-container">
          <h2>{t(`${p}.cta_title`)}</h2>
          <p>{t(`${p}.cta_desc`)}</p>
          <div className="sp-cta-btns">
            <Link to={ROUTES.contact} className="pl-btn pl-btn-ghost">{t(`${p}.cta_sales`)} →</Link>
            <Link to={ROUTES.calculator} className="pl-btn pl-btn-ghost">{t(`${p}.cta_calc`)}</Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: ContactPage — /contact
──────────────────────────────────────────────────── */
export function ContactPage() {
  // ─── Env Validation ──────────────────────────────────────────────
  const required = (key) => {
    const val = process.env[key];
    if (!val) throw new Error(`[ContactPage] Eksik env: ${key}`);
    return val;
  };
  const TURNSTILE_SITE_KEY = required('REACT_APP_TURNSTILE_SITE_KEY');
  const { t } = useTranslation();
  const p = 'public.static.static-pages.contact-page';

  const [form, setForm]             = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent]             = useState(false);
  const [loading, setLoading]       = useState(false);
  const [errors, setErrors]         = useState([]);
  const [successMsg, setSuccessMsg] = useState(null);

  const [turnstileToken,     setTurnstileToken]     = useState(null);
  const [turnstileReady,     setTurnstileReady]     = useState(false);
  const [turnstileWidgetId,  setTurnstileWidgetId]  = useState(null);
  const turnstileRef = useRef(null);

  useEffect(() => {
    const SCRIPT_ID = 'cf-turnstile-script';
    if (document.getElementById(SCRIPT_ID)) {
      setTurnstileReady(true);
      return;
    }
    const script    = document.createElement('script');
    script.id       = SCRIPT_ID;
    script.src      = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async    = true;
    script.defer    = true;
    script.onload   = () => setTurnstileReady(true);
    document.head.appendChild(script);
    return () => {
      if (turnstileWidgetId !== null && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!turnstileReady || !turnstileRef.current) return;
    if (turnstileWidgetId !== null) return;
    const id = window.turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme:   'light',
      callback: (token) => {
        setTurnstileToken(token);
        setErrors([]);
      },
      'error-callback': () => {
        setTurnstileToken(null);
        setErrors(['CAPTCHA dogrulamasi basarisiz. Lutfen sayfayi yenileyip tekrar deneyin.']);
      },
      'expired-callback': () => {
        setTurnstileToken(null);
      },
    });
    setTurnstileWidgetId(id);
  }, [turnstileReady, turnstileWidgetId]);

  const resetTurnstile = () => {
    if (window.turnstile && turnstileWidgetId !== null) {
      window.turnstile.reset(turnstileWidgetId);
    }
    setTurnstileToken(null);
  };

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const clientErrors = [];
    if (!form.name)      clientErrors.push(t(`${p}.form_name`)    + ' alani zorunludur.');
    if (!form.email)     clientErrors.push(t(`${p}.form_email`)   + ' alani zorunludur.');
    if (!form.message)   clientErrors.push(t(`${p}.form_message`) + ' alani zorunludur.');
    if (!turnstileToken) clientErrors.push('CAPTCHA dogrulamasini tamamlayin.');

    if (clientErrors.length > 0) {
      setErrors(clientErrors);
      return;
    }

    const payload = {
      name:           form.name,
      email:          form.email,
      subject:        form.subject,
      message:        form.message,
      turnstileToken: turnstileToken,
    };

    setLoading(true);
    setErrors([]);

    apiClient.post('/contact/send', payload)
      .then(res => {
        const body       = (res && res.isSucceed !== undefined) ? res : (res?.data ?? {});
        const isSucceed  = body?.isSucceed ?? false;

        if (isSucceed) {
          setSent(true);
          setErrors([]);
          setSuccessMsg(t(`${p}.form_success`));
        } else {
          const msgs = Array.isArray(body?.messages) ? body.messages : [];
          const readable = msgs
            .map(m => {
              const text = (m?.message ?? '').trim();
              if (/^\?.*\?$/.test(text)) return null;
              return text || null;
            })
            .filter(Boolean);

          setErrors(
            readable.length > 0
              ? readable
              : ['Bir hata olustu. Lutfen tekrar deneyin.']
          );
          resetTurnstile();
        }
      })
      .catch((err) => {
        const errData = err?.response?.data ?? err?.data ?? null;

        if (errData && errData.isSucceed === false) {
          const msgs = Array.isArray(errData.messages) ? errData.messages : [];
          const readable = msgs
            .map(m => {
              const text = (m?.message ?? '').trim();
              if (/^\?.*\?$/.test(text)) return null;
              return text || null;
            })
            .filter(Boolean);

          setErrors(
            readable.length > 0
              ? readable
              : ['Bir hata olustu. Lutfen tekrar deneyin.']
          );
        } else if (err?.message && !/^\?.*\?$/.test(err.message.trim())) {
          setErrors([err.message]);
        } else {
          setErrors(['Bir hata olustu. Lutfen tekrar deneyin.']);
        }

        resetTurnstile();
      })
      .finally(() => setLoading(false));
  };

  const canSubmit = !!(form.name && form.email && form.message && turnstileToken && !loading);

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .ct-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .ct-govde { padding: 36px 0; }
        .ct-grid { display: grid; grid-template-columns: 1fr 280px; gap: 32px; align-items: start; }
        .ct-form { border: 1px solid ${T.border}; border-radius: 4px; padding: 28px; background: #fff; }
        .ct-fields { display: flex; flex-direction: column; gap: 16px; }
        .ct-channel-list { display: flex; flex-direction: column; gap: 8px; }
        .ct-channel { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid ${T.border}; text-decoration: none; }
        .ct-channel:last-child { border-bottom: none; }
        .ct-ch-icon { width: 34px; height: 34px; border-radius: 3px; background: ${T.blueLt}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .ct-ch-label { font-size: 13px; font-weight: 600; color: ${T.navy}; }
        .ct-success { text-align: center; padding: 40px 0; }
        .ct-error-box { font-size: 13px; color: #c0392b; background: #fdf2f2; border: 1px solid #f5c6c6; border-radius: 4px; padding: 10px 14px; }
        .ct-error-box ul { margin: 6px 0 0 0; padding-left: 18px; }
        .ct-error-box ul li { margin-bottom: 3px; }
        .ct-error-box ul li:last-child { margin-bottom: 0; }
        .ct-turnstile-wrap { min-height: 65px; }

        /* ── Firma Bilgileri ── */
        .ct-company-info { margin-top: 20px; border-top: 1px solid ${T.border}; padding-top: 16px; display: flex; flex-direction: column; gap: 10px; }
        .ct-company-info-title { font-size: 11px; font-weight: 700; color: ${T.navy}; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 2px; }
        .ct-company-row { display: flex; align-items: flex-start; gap: 10px; }
        .ct-company-row-icon { width: 18px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; padding-top: 1px; }
        .ct-company-row-text { font-size: 12px; color: ${T.muted}; line-height: 1.5; }
        .ct-company-row-text a { color: ${T.blue}; text-decoration: none; }
        .ct-company-row-text a:hover { text-decoration: underline; }
        .ct-company-legal { margin-top: 6px; border-top: 1px solid ${T.border}; padding-top: 10px; display: flex; flex-direction: column; gap: 5px; }
        .ct-company-legal-row { font-size: 11px; color: ${T.muted}; line-height: 1.4; }
        .ct-company-legal-row span { font-weight: 600; color: ${T.navy}; }
        .ct-ito-note { font-size: 11px; color: ${T.muted}; line-height: 1.5; margin-top: 6px; border-top: 1px solid ${T.border}; padding-top: 10px; }
        .ct-ito-note a { color: ${T.blue}; text-decoration: none; }
        .ct-ito-note a:hover { text-decoration: underline; }

        @media (max-width: 768px) {
          .ct-grid { grid-template-columns: 1fr; gap: 28px; }
          .ct-hero { padding: 28px 0; }
          .ct-govde { padding: 28px 0; }
        }
        @media (max-width: 480px) {
          .ct-hero { padding: 20px 0; }
          .ct-govde { padding: 20px 0; }
          .ct-form { padding: 20px; }
        }
      `}</style>

      <div className="ct-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 700, color: T.navy, marginBottom: 12 }}>
            {t(`${p}.hero_title`)}
          </h1>
          <p style={{ fontSize: 15, color: T.muted, margin: 0, maxWidth: 520 }}>
            {t(`${p}.hero_desc`)}
          </p>
        </div>
      </div>

      <div className="ct-govde">
        <div className="pl-container">
          <div className="ct-grid">
            {/* ── Sol: İletişim Formu ── */}
            <div className="ct-form">
              {sent ? (
                <div className="ct-success">
                  <div style={{ fontSize: 42, marginBottom: 16 }}>✅</div>
                  <h3 style={{ color: T.navy, fontSize: 17, fontWeight: 600, marginBottom: 8 }}>
                    {successMsg || t(`${p}.form_success`)}
                  </h3>
                  <p style={{ fontSize: 14, color: T.muted, margin: '0 auto', maxWidth: 340 }}>
                    {t(`${p}.form_success_desc`)}
                  </p>
                </div>
              ) : (
                <form className="ct-fields" onSubmit={handleSubmit} noValidate>
                  <FormInput label={t(`${p}.form_name`)}    name="name"    value={form.name}    onChange={handleChange} />
                  <FormInput label={t(`${p}.form_email`)}   type="email"   name="email"   value={form.email}   onChange={handleChange} />
                  <FormInput label={t(`${p}.form_subject`)} name="subject" value={form.subject} onChange={handleChange} />
                  <FormInput label={t(`${p}.form_message`)} name="message" textarea value={form.message} onChange={handleChange} />

                  <div className="ct-turnstile-wrap">
                    <div ref={turnstileRef} />
                  </div>

                  {errors.length > 0 && (
                    <div className="ct-error-box" role="alert">
                      {errors.length === 1 ? (
                        <span>{errors[0]}</span>
                      ) : (
                        <ul>
                          {errors.map((msg, i) => <li key={i}>{msg}</li>)}
                        </ul>
                      )}
                    </div>
                  )}

                  <div>
                    <SubmitButton
                      label={loading ? t(`${p}.form_sending`) : t(`${p}.form_submit`)}
                      onClick={handleSubmit}
                      type="button"
                      disabled={!canSubmit}
                    />
                  </div>
                </form>
              )}
            </div>

            {/* ── Sağ: Kanallar + Firma Bilgileri ── */}
            <div>
              {/* İletişim Kanalları */}
              <div className="ct-channel-list">
                <div style={{ fontSize: 11, fontWeight: 700, color: T.navy, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
                  {t(`${p}.channels_title`)}
                </div>
                {[
                  { icon: 'fa-envelope',  label: t(`${p}.channel_email`),   href: 'mailto:info@tatalli.com.tr' },
                  { icon: 'fa-life-ring', label: t(`${p}.channel_support`), href: '#' },
                  /*{ icon: 'fa-book', label: t(`${p}.channel_docs`), href: ROUTES.docs },*/
                ].map((ch, i) => (
                  <a key={i} href={ch.href} className="ct-channel">
                    <div className="ct-ch-icon">
                      <i className={`fas ${ch.icon}`} style={{ color: T.blue, fontSize: 13 }} />
                    </div>
                    <span className="ct-ch-label">{ch.label}</span>
                    <i className="fas fa-external-link-alt" style={{ color: T.light, fontSize: 10, marginLeft: 'auto' }} />
                  </a>
                ))}
              </div>

              {/* Firma Bilgileri */}
              <div className="ct-company-info">
                <div className="ct-company-info-title">Firma Bilgileri</div>

                {/* Adres */}
                <div className="ct-company-row">
                  <div className="ct-company-row-icon">
                    <i className="fas fa-map-marker-alt" style={{ color: T.blue, fontSize: 12 }} />
                  </div>
                  <div className="ct-company-row-text">
                    Tatalli Teknoloji ve Ticaret Ltd. Şti.<br />
                    Ünalan Mah. Libadiye Cad. Emar Residence Blok<br />
                    No: 82E İç Kapı No: 291<br />
                    Üsküdar / İstanbul
                  </div>
                </div>

                {/* Telefon */}
                <div className="ct-company-row">
                  <div className="ct-company-row-icon">
                    <i className="fas fa-phone" style={{ color: T.blue, fontSize: 12 }} />
                  </div>
                  <div className="ct-company-row-text">
                    <a href="tel:+908503092950">+90 (850) 309 2950</a>
                  </div>
                </div>

                {/* E-posta */}
                <div className="ct-company-row">
                  <div className="ct-company-row-icon">
                    <i className="fas fa-envelope" style={{ color: T.blue, fontSize: 12 }} />
                  </div>
                  <div className="ct-company-row-text">
                    <a href="mailto:info@tatalli.com.tr">info@tatalli.com.tr</a>
                  </div>
                </div>

                {/* Yasal / Vergi Bilgileri */}
                <div className="ct-company-legal">
                  <div className="ct-company-legal-row">
                    <span>Vergi Dairesi:</span> Ümraniye VD. — 8300897383
                  </div>
                  <div className="ct-company-legal-row">
                    <span>Ticaret Sicil No:</span> 435859-5
                  </div>
                  <div className="ct-company-legal-row">
                    <span>MERSİS No:</span> 0830089738300001
                  </div>
                  <div className="ct-company-legal-row">
                    <span>KEP Adresi:</span> tatalli@hs01.kep.tr
                  </div>
                </div>

                {/* İTO Notu */}
                <div className="ct-ito-note">
                  Üyesi olduğumuz İstanbul Ticaret Odası'nın üyeleri için geçerli davranış kurallarına{' '}
                  <a href="https://www.ito.org.tr" target="_blank" rel="noopener noreferrer">
                    www.ito.org.tr
                  </a>{' '}
                  adresinden ulaşılabilir.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: SalesPage — /sales
──────────────────────────────────────────────────── */
export function SalesPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.sales-page';
  const sizes = t(`${p}.size_options`, { returnObjects: true }) || [];
  const [form, setForm] = useState({ company: '', name: '', email: '', size: sizes[0] || '', message: '' });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = () => {
    if (!form.company || !form.email) return;
    setLoading(true);
    setTimeout(() => { setLoading(false); setSent(true); }, 900);
  };

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .sl-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .sl-govde { padding: 36px 0; }
        .sl-form { border: 1px solid ${T.border}; border-radius: 4px; padding: 28px; background: #fff; max-width: 540px; }
        .sl-fields { display: flex; flex-direction: column; gap: 16px; }
        .sl-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .sl-select { padding: 10px 12px; border: 1px solid ${T.border}; border-radius: 2px; font-size: 13px; font-family: inherit; outline: none; color: ${T.navy}; background: #fff; width: 100%; }
        .sl-success { text-align: center; padding: 28px 0; }
        @media (max-width: 600px) {
          .sl-2col { grid-template-columns: 1fr; }
          .sl-hero { padding: 28px 0; }
          .sl-govde { padding: 28px 0; }
        }
        @media (max-width: 480px) {
          .sl-hero { padding: 20px 0; }
          .sl-govde { padding: 20px 0; }
          .sl-form { padding: 20px; }
        }
      `}</style>

      <div className="sl-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 700, color: T.navy, marginBottom: 12 }}>{t(`${p}.hero_title`)}</h1>
          <p style={{ fontSize: 15, color: T.muted, margin: 0, maxWidth: 520 }}>{t(`${p}.hero_desc`)}</p>
        </div>
      </div>

      <div className="sl-govde">
        <div className="pl-container">
          <div className="sl-form">
            {sent ? (
              <div className="sl-success">
                <div style={{ fontSize: 36, marginBottom: 16 }}>🎉</div>
                <h3 style={{ color: T.navy, marginBottom: 8 }}>{t(`${p}.form_success`)}</h3>
                <p style={{ color: T.muted, fontSize: 13 }}>{t(`${p}.form_success_desc`)}</p>
              </div>
            ) : (
              <div className="sl-fields">
                <FormInput label={t(`${p}.form_company`)} name="company" value={form.company} onChange={handleChange} />
                <div className="sl-2col">
                  <FormInput label={t(`${p}.form_name`)} name="name" value={form.name} onChange={handleChange} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: T.navy }}>{t(`${p}.form_size`)}</label>
                    <select name="size" value={form.size} onChange={handleChange} className="sl-select">
                      {sizes.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <FormInput label={t(`${p}.form_email`)} type="email" name="email" value={form.email} onChange={handleChange} />
                <FormInput label={t(`${p}.form_message`)} name="message" textarea value={form.message} onChange={handleChange} />
                <div><SubmitButton label={t(`${p}.form_submit`)} onClick={handleSubmit} disabled={loading} /></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: PrivacyPage — /privacy
──────────────────────────────────────────────────── */
export function PrivacyPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.privacy-page';
  const sections = t(`${p}.sections`, { returnObjects: true }) || [];

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .lg-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .lg-govde { padding: 36px 0; }
        .lg-content { max-width: 720px; color: ${T.muted}; line-height: 1.9; }
        .lg-section { margin-bottom: 28px; }
        .lg-section h3 { font-size: 15px; font-weight: 700; color: ${T.navy}; margin: 0 0 8px; }
        .lg-section p { margin: 0; font-size: 14px; }
        .lg-meta { font-size: 12px; color: ${T.light}; margin-bottom: 32px; }
        .lg-intro { font-size: 14px; margin-bottom: 32px; }
        @media (max-width: 768px) {
          .lg-hero { padding: 28px 0; }
          .lg-govde { padding: 28px 0; }
        }
        @media (max-width: 480px) {
          .lg-hero { padding: 20px 0; }
          .lg-govde { padding: 20px 0; }
        }
      `}</style>

      <div className="lg-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 700, color: T.navy }}>{t(`${p}.hero_title`)}</h1>
        </div>
      </div>

      <div className="lg-govde">
        <div className="pl-container">
          <div className="lg-content">
            <p className="lg-meta">{t(`${p}.last_updated`)}</p>
            <p className="lg-intro">{t(`${p}.intro`)}</p>
            {sections.map((s, i) => (
              <div key={i} className="lg-section">
                <h3>{s.heading}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: TermsPage — /terms
──────────────────────────────────────────────────── */
export function TermsPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.terms-page';
  const sections = t(`${p}.sections`, { returnObjects: true }) || [];

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .lg-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .lg-govde { padding: 36px 0; }
        .lg-content { max-width: 720px; color: ${T.muted}; line-height: 1.9; }
        .lg-section { margin-bottom: 28px; }
        .lg-section h3 { font-size: 15px; font-weight: 700; color: ${T.navy}; margin: 0 0 8px; }
        .lg-section p { margin: 0; font-size: 14px; }
        .lg-meta { font-size: 12px; color: ${T.light}; margin-bottom: 32px; }
        .lg-intro { font-size: 14px; margin-bottom: 32px; }
        @media (max-width: 768px) {
          .lg-hero { padding: 28px 0; }
          .lg-govde { padding: 28px 0; }
        }
        @media (max-width: 480px) {
          .lg-hero { padding: 20px 0; }
          .lg-govde { padding: 20px 0; }
        }
      `}</style>

      <div className="lg-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 700, color: T.navy }}>{t(`${p}.hero_title`)}</h1>
        </div>
      </div>

      <div className="lg-govde">
        <div className="pl-container">
          <div className="lg-content">
            <p className="lg-meta">{t(`${p}.last_updated`)}</p>
            <p className="lg-intro">{t(`${p}.intro`)}</p>
            {sections.map((s, i) => (
              <div key={i} className="lg-section">
                <h3>{s.heading}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: SlaPage — /sla
──────────────────────────────────────────────────── */
export function SlaPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.sla-page';

  const uptime_table = t(`${p}.uptime_table`, { returnObjects: true }) || [];
  const credit_table = t(`${p}.credit_table`, { returnObjects: true }) || [];

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .sl-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .sl-govde { padding: 36px 0; }
        .sl-section { margin-bottom: 48px; }
        .sl-section:last-child { margin-bottom: 0; }
        .sl-table-wrap { border: 1px solid ${T.border}; border-radius: 4px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .sl-table-wrap table { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 500px; }
        .sl-table-wrap thead tr { background: ${T.bg}; border-bottom: 1px solid ${T.border}; }
        .sl-table-wrap th { padding: 12px 16px; text-align: left; font-weight: 700; color: ${T.navy}; white-space: nowrap; }
        .sl-table-wrap th.center { text-align: center; }
        .sl-table-wrap tbody td { padding: 12px 16px; border-bottom: 1px solid ${T.border}; }
        .sl-table-wrap tbody tr:nth-child(even) { background: ${T.bg}; }
        .sl-table-wrap tbody td.center { text-align: center; }
        .sl-table-wrap tbody td.green { color: ${T.green}; font-weight: 700; }
        .sl-alt { background: ${T.bg}; }
        .sl-credit { max-width: 440px; }
        .sl-note { font-size: 14px; color: ${T.muted}; margin-bottom: 24px; }
        .sl-table-dark thead tr { background: ${T.navy}; }
        .sl-table-dark th { color: #fff; }
        .sl-table-dark tbody td.credit { color: #b91c1c; font-weight: 700; }
        @media (max-width: 768px) {
          .sl-table-wrap { margin: 0 -12px; }
          .sl-hero { padding: 28px 0; }
          .sl-govde { padding: 28px 0; }
          .sl-section { margin-bottom: 36px; }
        }
        @media (max-width: 480px) {
          .sl-hero { padding: 20px 0; }
          .sl-govde { padding: 20px 0; }
          .sl-section { margin-bottom: 28px; }
        }
      `}</style>

      <div className="sl-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 700, color: T.navy, marginBottom: 12 }}>{t(`${p}.hero_title`)}</h1>
          <p style={{ fontSize: 15, color: T.muted, margin: 0, maxWidth: 520 }}>{t(`${p}.hero_desc`)}</p>
        </div>
      </div>

      <div className="sl-govde">
        <div className="pl-container">
          <div className="sl-section">
            <h2 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 20 }}>{t(`${p}.uptime_title`)}</h2>
            <div className="sl-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t(`${p}.col_service`)}</th>
                    <th className="center">{t(`${p}.col_uptime`)}</th>
                    <th className="center">{t(`${p}.col_downtime`)}</th>
                  </tr>
                </thead>
                <tbody>
                  {uptime_table.map((row, i) => (
                    <tr key={i}>
                      <td>{row.service}</td>
                      <td className="center green">{row.uptime}</td>
                      <td className="center">{row.downtime_mo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="sl-section sl-alt">
            <h2 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 10 }}>{t(`${p}.credits_title`)}</h2>
            <p className="sl-note">{t(`${p}.credit_desc`)}</p>
            <div className="sl-table-wrap sl-credit sl-table-dark">
              <table>
                <thead>
                  <tr>
                    <th>{t(`${p}.col_monthly_uptime`)}</th>
                    <th className="center">{t(`${p}.col_credit`)}</th>
                  </tr>
                </thead>
                <tbody>
                  {credit_table.map((row, i) => (
                    <tr key={i}>
                      <td>{row.range}</td>
                      <td className="center credit">{row.credit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: LegalPage — /legal
──────────────────────────────────────────────────── */
export function LegalPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.legal-page';

  const doc_titles = t(`${p}.doc_titles`, { returnObjects: true }) || [];
  // href ve icon dil-bağımsız teknik veriler — kodda tutulur, sıra doc_titles ile eşleşmelidir
  const DOCS = [
    { title: doc_titles[0], href: ROUTES.terms, icon: 'fa-file-contract' },
    { title: doc_titles[1], href: ROUTES.privacy, icon: 'fa-user-shield' },
    { title: doc_titles[2], href: ROUTES.sla, icon: 'fa-medal' },
    { title: doc_titles[3], href: '#', icon: 'fa-balance-scale' },
    { title: doc_titles[4], href: '#', icon: 'fa-cookie-bite' },
    { title: doc_titles[5], href: '#', icon: 'fa-file-signature' },
  ];

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        .lp-hero { background: ${T.bg}; border-bottom: 1px solid ${T.border}; padding: 36px 0; }
        .lp-govde { padding: 36px 0; }
        .lp-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; margin-bottom: 36px; }
        .lp-card { display: flex; align-items: center; gap: 12px; padding: 16px 14px; background: #fff; border: 1px solid ${T.border}; border-radius: 3px; text-decoration: none; transition: border 0.12s; }
        .lp-card:hover { border-color: ${T.blue}; }
        .lp-icon { width: 32px; height: 32px; border-radius: 3px; background: ${T.blueLt}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .lp-title { font-size: 13px; font-weight: 600; color: ${T.navy}; line-height: 1.3; flex: 1; }
        .lp-arrow { color: ${T.light}; font-size: 10px; }
        .lp-box { padding: 20px 16px; background: ${T.bg}; border: 1px solid ${T.border}; border-radius: 3px; max-width: 420px; }
        .lp-label { font-size: 11px; font-weight: 700; color: ${T.navy}; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 8px; }
        .lp-text { font-size: 13px; color: ${T.muted}; margin: 0; line-height: 1.8; }
        @media (max-width: 768px) {
          .lp-hero { padding: 28px 0; }
          .lp-govde { padding: 28px 0; }
          .lp-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
        }
        @media (max-width: 480px) {
          .lp-hero { padding: 20px 0; }
          .lp-govde { padding: 20px 0; }
          .lp-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="lp-hero">
        <div className="pl-container">
          <Breadcrumb label={t(`${p}.breadcrumb`)} />
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 700, color: T.navy }}>{t(`${p}.hero_title`)}</h1>
        </div>
      </div>

      <div className="lp-govde">
        <div className="pl-container">
          <h2 style={{ fontSize: 24, fontWeight: 700, color: T.navy, marginBottom: 24 }}>{t(`${p}.sections_title`)}</h2>
          <div className="lp-grid">
            {DOCS.map((doc, i) => (
              <a key={i} href={doc.href} className="lp-card">
                <div className="lp-icon"><i className={`fas ${doc.icon}`} style={{ color: T.blue, fontSize: 13 }} /></div>
                <span className="lp-title">{doc.title}</span>
                <i className="fas fa-arrow-right lp-arrow" />
              </a>
            ))}
          </div>

          <div className="lp-box">
            <div className="lp-label">{t(`${p}.company_info_label`)}</div>
            <p className="lp-text">
              {t(`${p}.company`)}<br />
              {t(`${p}.address`)}<br />
              {t(`${p}.registration`)}
            </p>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: PricingPurchaseOptionsPage — /pricing/purchase-options
──────────────────────────────────────────────────── */
export function PricingPurchaseOptionsPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.pricing-purchase-options';

  const stats = t(`${p}.stats.items`, { returnObjects: true }) || [];
  const payg_features = t(`${p}.payg.features`, { returnObjects: true }) || [];
  const committed_features = t(`${p}.committed.features`, { returnObjects: true }) || [];
  const comparison_rows = t(`${p}.comparison.rows`, { returnObjects: true }) || [];
  const faq_items = t(`${p}.faq.items`, { returnObjects: true }) || [];

  // Teknik veriler — ikonlar ve link'ler kodda, metin i18n'de
  const STATS_ICONS = ['fa-shield-alt', 'fa-globe-europe', 'fa-users', 'fa-headset'];
  const PAYG_ICONS = ['fa-credit-card', 'fa-stopwatch', 'fa-expand-arrows-alt', 'fa-globe-americas', 'fa-file-invoice-dollar', 'fa-unlock-alt'];
  const COMMITTED_ICONS = ['fa-handshake', 'fa-percent', 'fa-server', 'fa-headset', 'fa-user-tie', 'fa-file-signature', 'fa-calendar-check'];
  const COMPARISON_ICONS = ['fa-handshake', 'fa-tag', 'fa-arrows-alt', 'fa-headset', 'fa-shield-alt', 'fa-calendar-alt', 'fa-sign-out-alt'];

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        /* HERO */
        .po-hero { position: relative; background: linear-gradient(135deg, ${T.bg} 0%, #fff 100%); border-bottom: 1px solid ${T.border}; padding: 56px 0 64px; overflow: hidden; }
        .po-hero-inner { position: relative; z-index: 2; max-width: 720px; }
        .po-hero-eyebrow { display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; background: ${T.blueLt}; color: ${T.blue}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; border-radius: 3px; margin-bottom: 16px; }
        .po-hero-sub { font-size: 15px; color: ${T.muted}; margin-top: 14px; max-width: 680px; line-height: 1.7; }
        .po-hero-decor { position: absolute; top: 0; right: -40px; bottom: 0; width: 480px; z-index: 1; pointer-events: none; opacity: 0.85; }

        /* STATS BAR */
        .po-stats { background: #fff; border-bottom: 1px solid ${T.border}; padding: 28px 0; }
        .po-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
        .po-stat { display: flex; align-items: center; gap: 14px; }
        .po-stat-icon { width: 44px; height: 44px; border-radius: 8px; background: ${T.blueLt}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .po-stat-icon i { color: ${T.blue}; font-size: 18px; }
        .po-stat-value { font-size: 22px; font-weight: 800; color: ${T.navy}; line-height: 1.1; letter-spacing: -0.3px; }
        .po-stat-label { font-size: 12px; color: ${T.muted}; margin-top: 3px; }

        /* BODY */
        .po-govde { padding: 56px 0; }
        .po-section { margin-bottom: 64px; }
        .po-section:last-child { margin-bottom: 0; }
        .po-section-title { font-size: 24px; font-weight: 700; color: ${T.navy}; margin: 0 0 10px 0; letter-spacing: -0.3px; }
        .po-section-sub { font-size: 14px; color: ${T.muted}; margin: 0 0 32px 0; max-width: 720px; line-height: 1.7; }

        /* OPTION CARDS */
        .po-options { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
        .po-option { position: relative; background: #fff; border: 1px solid ${T.border}; border-radius: 4px; padding: 32px 28px 28px; display: flex; flex-direction: column; transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s; overflow: hidden; }
        .po-option:hover { border-color: ${T.blue}; box-shadow: 0 8px 24px rgba(0,0,0,0.06); transform: translateY(-2px); }
        .po-option-featured { border-color: ${T.blue}; box-shadow: 0 4px 18px rgba(0,0,0,0.05); }
        .po-option-featured::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, ${T.blue} 0%, ${T.navy} 100%); }

        /* Savings ribbon */
        .po-ribbon { position: absolute; top: 18px; right: -42px; transform: rotate(35deg); background: linear-gradient(135deg, #16a34a 0%, #15803d 100%); color: #fff; padding: 5px 50px; font-size: 10px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; box-shadow: 0 2px 6px rgba(22,163,74,0.3); z-index: 3; }

        .po-option-header { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
        .po-option-icon { width: 56px; height: 56px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .po-option-icon-payg { background: linear-gradient(135deg, ${T.blueLt} 0%, #dbeafe 100%); }
        .po-option-icon-committed { background: linear-gradient(135deg, ${T.blue} 0%, ${T.navy} 100%); }
        .po-option-icon-payg i { color: ${T.blue}; font-size: 22px; }
        .po-option-icon-committed i { color: #fff; font-size: 22px; }
        .po-option-titles { flex: 1; min-width: 0; }
        .po-option-title { font-size: 19px; font-weight: 700; color: ${T.navy}; margin: 0 0 4px 0; letter-spacing: -0.2px; }
        .po-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; padding: 3px 8px; border-radius: 3px; background: ${T.blueLt}; color: ${T.blue}; }

        .po-option-desc { font-size: 13px; color: ${T.muted}; line-height: 1.7; margin: 0 0 20px 0; }

        /* Price block */
        .po-price-block { padding: 18px 0; border-top: 1px solid ${T.border}; border-bottom: 1px solid ${T.border}; margin-bottom: 22px; }
        .po-price-from { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.7px; color: ${T.muted}; margin-bottom: 6px; }
        .po-price-row { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
        .po-price-amount { font-size: 32px; font-weight: 800; color: ${T.navy}; line-height: 1; letter-spacing: -1px; }
        .po-price-unit { font-size: 13px; color: ${T.muted}; font-weight: 500; }

        /* Features with per-item icons */
        .po-features { list-style: none; padding: 0; margin: 0 0 24px 0; flex: 1; }
        .po-feature { display: flex; align-items: flex-start; gap: 12px; font-size: 13px; color: ${T.navy}; padding: 9px 0; line-height: 1.5; }
        .po-feature-icon-wrap { width: 26px; height: 26px; border-radius: 6px; background: ${T.blueLt}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: -1px; }
        .po-feature-icon-wrap i { color: ${T.blue}; font-size: 11px; }
        .po-feature-text { flex: 1; }

        /* CTA buttons */
        .po-cta { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 12px 22px; background: ${T.blue}; color: #fff; text-decoration: none; font-size: 13px; font-weight: 600; border-radius: 3px; transition: opacity 0.15s, transform 0.15s; align-self: stretch; }
        .po-cta:hover { opacity: 0.92; transform: translateY(-1px); }
        .po-cta-dark { background: ${T.navy}; }
        .po-cta-secondary { background: #fff; color: ${T.blue}; border: 1px solid ${T.blue}; }
        .po-note { font-size: 11px; color: ${T.muted}; margin: 14px 0 0 0; line-height: 1.6; }

        /* COMPARISON TABLE */
        .po-table-wrap { overflow-x: auto; border: 1px solid ${T.border}; border-radius: 4px; background: #fff; }
        .po-table { width: 100%; border-collapse: collapse; min-width: 620px; }
        .po-table th { background: ${T.bg}; padding: 16px 18px; text-align: left; font-size: 11px; font-weight: 700; color: ${T.navy}; text-transform: uppercase; letter-spacing: 0.6px; border-bottom: 1px solid ${T.border}; }
        .po-table th:not(:first-child) { text-align: center; }
        .po-table td { padding: 16px 18px; font-size: 13px; color: ${T.navy}; border-bottom: 1px solid ${T.border}; line-height: 1.5; }
        .po-table td:not(:first-child) { text-align: center; color: ${T.muted}; }
        .po-table tr:last-child td { border-bottom: none; }
        .po-table tr:hover td { background: ${T.bg}; }
        .po-table-feature { display: flex; align-items: center; gap: 10px; font-weight: 600; }
        .po-table-feature-icon { width: 26px; height: 26px; border-radius: 6px; background: ${T.blueLt}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .po-table-feature-icon i { color: ${T.blue}; font-size: 11px; }

        /* FAQ */
        .po-faq-item { display: flex; gap: 14px; background: #fff; border: 1px solid ${T.border}; border-radius: 4px; padding: 20px 22px; margin-bottom: 10px; transition: border-color 0.15s; }
        .po-faq-item:hover { border-color: ${T.blue}; }
        .po-faq-item:last-child { margin-bottom: 0; }
        .po-faq-q-icon { width: 28px; height: 28px; border-radius: 6px; background: ${T.blueLt}; color: ${T.blue}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 12px; font-weight: 800; font-family: Georgia, serif; }
        .po-faq-body { flex: 1; }
        .po-faq-q { font-size: 14px; font-weight: 600; color: ${T.navy}; margin: 0 0 8px 0; line-height: 1.4; }
        .po-faq-a { font-size: 13px; color: ${T.muted}; line-height: 1.7; margin: 0; }

        /* BOTTOM CTA */
        .po-bottom { position: relative; background: linear-gradient(135deg, ${T.navy} 0%, ${T.blue} 100%); border-radius: 4px; padding: 48px 36px; text-align: center; overflow: hidden; color: #fff; }
        .po-bottom-decor { position: absolute; top: 0; right: 0; bottom: 0; left: 0; opacity: 0.08; pointer-events: none; }
        .po-bottom-inner { position: relative; z-index: 2; }
        .po-bottom-title { font-size: 22px; font-weight: 700; color: #fff; margin: 0 0 10px 0; letter-spacing: -0.3px; }
        .po-bottom-sub { font-size: 14px; color: rgba(255,255,255,0.85); margin: 0 auto 24px auto; max-width: 600px; line-height: 1.7; }
        .po-bottom-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
        .po-bottom-actions .po-cta { align-self: auto; background: #fff; color: ${T.navy}; }
        .po-bottom-actions .po-cta:hover { background: ${T.bg}; }
        .po-bottom-actions .po-cta-outline { background: transparent; color: #fff; border: 1px solid rgba(255,255,255,0.4); }
        .po-bottom-actions .po-cta-outline:hover { background: rgba(255,255,255,0.1); }

        @media (max-width: 920px) {
          .po-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 20px; }
          .po-hero-decor { opacity: 0.4; width: 360px; right: -80px; }
        }
        @media (max-width: 768px) {
          .po-hero { padding: 36px 0 40px; }
          .po-govde { padding: 36px 0; }
          .po-section { margin-bottom: 44px; }
          .po-options { grid-template-columns: 1fr; gap: 16px; }
          .po-option { padding: 26px 22px 24px; }
          .po-bottom { padding: 36px 24px; }
        }
        @media (max-width: 480px) {
          .po-hero { padding: 28px 0 32px; }
          .po-hero-decor { display: none; }
          .po-stats-grid { grid-template-columns: 1fr; gap: 14px; }
          .po-govde { padding: 28px 0; }
          .po-option-header { gap: 12px; }
          .po-option-icon { width: 48px; height: 48px; }
          .po-price-amount { font-size: 28px; }
        }
      `}</style>

      {/* HERO */}
      <div className="po-hero">
        <svg className="po-hero-decor" viewBox="0 0 480 320" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="380" cy="80" r="70" fill={T.blue} opacity="0.07" />
          <circle cx="280" cy="160" r="95" fill={T.blue} opacity="0.05" />
          <circle cx="420" cy="220" r="65" fill={T.navy} opacity="0.06" />
          <circle cx="180" cy="60" r="40" fill={T.blue} opacity="0.08" />
          <g transform="translate(260,100)" opacity="0.9">
            <path d="M50 40 C50 22, 65 8, 85 8 C100 8, 113 18, 117 32 C135 32, 148 46, 148 62 C148 78, 135 92, 117 92 L55 92 C38 92, 25 78, 25 62 C25 50, 35 40, 50 40 Z"
              fill="#fff" stroke={T.blue} strokeWidth="1.5" opacity="0.9" />
            <path d="M90 35 L78 60 L92 60 L82 85 L102 56 L88 56 L98 35 Z" fill={T.blue} opacity="0.9" />
          </g>
          <path d="M40 250 Q 140 200, 240 240 T 440 230" stroke={T.blue} strokeWidth="1.5" strokeDasharray="3,6" fill="none" opacity="0.3" />
        </svg>

        <div className="pl-container">
          <div className="po-hero-inner">
            <Breadcrumb label={t(`${p}.breadcrumb`)} />
            <div className="po-hero-eyebrow">
              <i className="fas fa-cloud" style={{ fontSize: 11 }} />
              {t(`${p}.hero_eyebrow`)}
            </div>
            <h1 style={{ fontSize: 'clamp(24px, 4.2vw, 36px)', fontWeight: 700, color: T.navy, margin: 0, letterSpacing: '-0.5px', lineHeight: 1.2 }}>
              {t(`${p}.hero_title`)}
            </h1>
            <p className="po-hero-sub">{t(`${p}.hero_subtitle`)}</p>
          </div>
        </div>
      </div>

      {/* STATS BAR */}
      <div className="po-stats">
        <div className="pl-container">
          <div className="po-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="po-stat">
                <div className="po-stat-icon">
                  <i className={`fas ${STATS_ICONS[i] || 'fa-check'}`} />
                </div>
                <div>
                  <div className="po-stat-value">{s.value}</div>
                  <div className="po-stat-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="po-govde">
        <div className="pl-container">

          {/* OPTIONS */}
          <section className="po-section">
            <h2 className="po-section-title">{t(`${p}.options_title`)}</h2>
            <p className="po-section-sub">{t(`${p}.options_subtitle`)}</p>

            <div className="po-options">
              {/* Pay-as-you-go */}
              <div className="po-option">
                <div className="po-option-header">
                  <div className="po-option-icon po-option-icon-payg">
                    <i className="fas fa-bolt" />
                  </div>
                  <div className="po-option-titles">
                    <h3 className="po-option-title">{t(`${p}.payg.title`)}</h3>
                    <span className="po-badge">
                      <i className="fas fa-infinity" style={{ fontSize: 9 }} />
                      {t(`${p}.payg.badge`)}
                    </span>
                  </div>
                </div>
                <p className="po-option-desc">{t(`${p}.payg.description`)}</p>

                <div className="po-price-block">
                  <div className="po-price-from">{t(`${p}.payg.price_from`)}</div>
                  <div className="po-price-row">
                    <span className="po-price-amount">{t(`${p}.payg.price_amount`)}</span>
                    <span className="po-price-unit">{t(`${p}.payg.price_unit`)}</span>
                  </div>
                </div>

                <ul className="po-features">
                  {payg_features.map((f, i) => (
                    <li key={i} className="po-feature">
                      <span className="po-feature-icon-wrap">
                        <i className={`fas ${PAYG_ICONS[i] || 'fa-check'}`} />
                      </span>
                      <span className="po-feature-text">{f}</span>
                    </li>
                  ))}
                </ul>

                <a href={ROUTES.contact} className="po-cta">
                  {t(`${p}.payg.cta`)}
                  <i className="fas fa-arrow-right" style={{ fontSize: 11 }} />
                </a>
                <p className="po-note">{t(`${p}.payg.note`)}</p>
              </div>

              {/* Sabit Aylık Plan */}
              <div className="po-option po-option-featured">
                <div className="po-ribbon">{t(`${p}.committed.savings_ribbon`)}</div>

                <div className="po-option-header">
                  <div className="po-option-icon po-option-icon-committed">
                    <i className="fas fa-shield-alt" />
                  </div>
                  <div className="po-option-titles">
                    <h3 className="po-option-title">{t(`${p}.committed.title`)}</h3>
                    <span className="po-badge">
                      <i className="fas fa-star" style={{ fontSize: 9 }} />
                      {t(`${p}.committed.badge`)}
                    </span>
                  </div>
                </div>
                <p className="po-option-desc">{t(`${p}.committed.description`)}</p>

                <div className="po-price-block">
                  <div className="po-price-from">{t(`${p}.committed.price_from`)}</div>
                  <div className="po-price-row">
                    <span className="po-price-amount">{t(`${p}.committed.price_amount`)}</span>
                    <span className="po-price-unit">{t(`${p}.committed.price_unit`)}</span>
                  </div>
                </div>

                <ul className="po-features">
                  {committed_features.map((f, i) => (
                    <li key={i} className="po-feature">
                      <span className="po-feature-icon-wrap">
                        <i className={`fas ${COMMITTED_ICONS[i] || 'fa-check'}`} />
                      </span>
                      <span className="po-feature-text">{f}</span>
                    </li>
                  ))}
                </ul>

                <a href={ROUTES.contact} className="po-cta po-cta-dark">
                  {t(`${p}.committed.cta`)}
                  <i className="fas fa-arrow-right" style={{ fontSize: 11 }} />
                </a>
                <p className="po-note">{t(`${p}.committed.note`)}</p>
              </div>
            </div>
          </section>

          {/* COMPARISON */}
          <section className="po-section">
            <h2 className="po-section-title">{t(`${p}.comparison.title`)}</h2>
            <p className="po-section-sub">{t(`${p}.comparison.subtitle`)}</p>
            <div className="po-table-wrap">
              <table className="po-table">
                <thead>
                  <tr>
                    <th>{t(`${p}.comparison.col_feature`)}</th>
                    <th>{t(`${p}.comparison.col_payg`)}</th>
                    <th>{t(`${p}.comparison.col_committed`)}</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison_rows.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <div className="po-table-feature">
                          <span className="po-table-feature-icon">
                            <i className={`fas ${COMPARISON_ICONS[i] || 'fa-circle'}`} />
                          </span>
                          {row.feature}
                        </div>
                      </td>
                      <td>{row.payg}</td>
                      <td>{row.committed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* FAQ */}
          <section className="po-section">
            <h2 className="po-section-title">{t(`${p}.faq.title`)}</h2>
            <p className="po-section-sub">{t(`${p}.faq.subtitle`)}</p>
            <div>
              {faq_items.map((item, i) => (
                <div key={i} className="po-faq-item">
                  <div className="po-faq-q-icon">Q</div>
                  <div className="po-faq-body">
                    <div className="po-faq-q">{item.q}</div>
                    <p className="po-faq-a">{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* BOTTOM CTA */}
          <section className="po-section">
            <div className="po-bottom">
              <svg className="po-bottom-decor" viewBox="0 0 800 300" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                <circle cx="80" cy="50" r="60" fill="#fff" />
                <circle cx="720" cy="240" r="80" fill="#fff" />
                <circle cx="600" cy="60" r="40" fill="#fff" />
                <circle cx="200" cy="250" r="50" fill="#fff" />
                <path d="M0 200 Q 200 140, 400 180 T 800 160" stroke="#fff" strokeWidth="2" fill="none" opacity="0.4" />
              </svg>
              <div className="po-bottom-inner">
                <h3 className="po-bottom-title">{t(`${p}.cta.title`)}</h3>
                <p className="po-bottom-sub">{t(`${p}.cta.subtitle`)}</p>
                <div className="po-bottom-actions">
                  <a href={ROUTES.contact} className="po-cta po-cta-outline">
                    {t(`${p}.cta.secondary`)}
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: ProductInfrastructureCloudInstancePage — /product/infrastructure/cloud-instance
──────────────────────────────────────────────────── */
export function ProductInfrastructureCloudInstancePage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.product.infrastructure.cloud-instance';

  const stats = t(`${p}.stats.items`, { returnObjects: true }) || [];
  const distros = t(`${p}.distros.items`, { returnObjects: true }) || [];
  const features = t(`${p}.features.items`, { returnObjects: true }) || [];
  const usecases = t(`${p}.usecases.items`, { returnObjects: true }) || [];
  const instance_rows = t(`${p}.instances.items`, { returnObjects: true }) || [];
  const faq_items = t(`${p}.faq.items`, { returnObjects: true }) || [];

  // Teknik veriler — ikonlar kodda, metin i18n'de
  const STATS_ICONS = ['fa-shield-alt', 'fa-server', 'fa-globe-europe', 'fa-headset'];

  // Linux dağıtım ikonları — mevcutsa brand icon, yoksa fa-linux
  const DISTRO_ICONS = [
    { prefix: 'fab', icon: 'fa-ubuntu' },
    { prefix: 'fab', icon: 'fa-redhat' },
    { prefix: 'fab', icon: 'fa-linux' },     // Debian
    { prefix: 'fab', icon: 'fa-linux' },     // Rocky Linux
    { prefix: 'fab', icon: 'fa-linux' },     // AlmaLinux
    { prefix: 'fab', icon: 'fa-suse' },
  ];

  const FEATURE_ICONS = [
    'fa-microchip', 'fa-hdd', 'fa-memory',
    'fa-network-wired', 'fa-cloud-upload-alt', 'fa-shield-alt',
    'fa-expand-arrows-alt', 'fa-key', 'fa-code',
  ];

  const USECASE_ICONS = ['fa-globe', 'fa-database', 'fa-cube', 'fa-flask', 'fa-chart-line', 'fa-rocket'];
  const INSTANCE_ICONS = ['fa-balance-scale', 'fa-bolt', 'fa-memory', 'fa-hdd'];

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        /* HERO */
        .po-hero { position: relative; background: linear-gradient(135deg, ${T.bg} 0%, #fff 100%); border-bottom: 1px solid ${T.border}; padding: 64px 0 72px; overflow: hidden; }
        .po-hero-inner { position: relative; z-index: 2; max-width: 680px; }
        .po-hero-eyebrow { display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; background: ${T.blueLt}; color: ${T.blue}; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; border-radius: 3px; margin-bottom: 16px; }
        .po-hero-sub { font-size: 15px; color: ${T.muted}; margin-top: 14px; max-width: 660px; line-height: 1.7; }
        .po-hero-actions { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 26px; }
        .po-hero-decor { position: absolute; top: 0; right: -40px; bottom: 0; width: 540px; z-index: 1; pointer-events: none; opacity: 0.95; }

        /* STATS BAR */
        .po-stats { background: #fff; border-bottom: 1px solid ${T.border}; padding: 28px 0; }
        .po-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; }
        .po-stat { display: flex; align-items: center; gap: 14px; }
        .po-stat-icon { width: 44px; height: 44px; border-radius: 8px; background: ${T.blueLt}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .po-stat-icon i { color: ${T.blue}; font-size: 18px; }
        .po-stat-value { font-size: 22px; font-weight: 800; color: ${T.navy}; line-height: 1.1; letter-spacing: -0.3px; }
        .po-stat-label { font-size: 12px; color: ${T.muted}; margin-top: 3px; }

        /* BODY */
        .po-govde { padding: 56px 0; }
        .po-section { margin-bottom: 64px; }
        .po-section:last-child { margin-bottom: 0; }
        .po-section-head { max-width: 720px; margin: 0 0 32px 0; }
        .po-section-title { font-size: 24px; font-weight: 700; color: ${T.navy}; margin: 0 0 10px 0; letter-spacing: -0.3px; }
        .po-section-sub { font-size: 14px; color: ${T.muted}; margin: 0; line-height: 1.7; }

        /* DISTRIBUTIONS */
        .po-distros { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
        .po-distro { position: relative; background: #fff; border: 1px solid ${T.border}; border-radius: 4px; padding: 24px; transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s; overflow: hidden; }
        .po-distro::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, ${T.blue} 0%, transparent 100%); opacity: 0; transition: opacity 0.15s; }
        .po-distro:hover { border-color: ${T.blue}; box-shadow: 0 6px 18px rgba(0,0,0,0.05); transform: translateY(-2px); }
        .po-distro:hover::before { opacity: 1; }
        .po-distro-head { display: flex; align-items: center; gap: 14px; margin-bottom: 14px; }
        .po-distro-icon { width: 52px; height: 52px; border-radius: 12px; background: linear-gradient(135deg, ${T.blueLt} 0%, #dbeafe 100%); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .po-distro-icon i { color: ${T.blue}; font-size: 26px; }
        .po-distro-titles { flex: 1; min-width: 0; }
        .po-distro-name { font-size: 15px; font-weight: 700; color: ${T.navy}; margin: 0 0 4px 0; line-height: 1.3; letter-spacing: -0.2px; }
        .po-distro-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; padding: 2px 7px; border-radius: 3px; background: ${T.blueLt}; color: ${T.blue}; }
        .po-distro-desc { font-size: 13px; color: ${T.muted}; line-height: 1.65; margin: 0; }

        /* FEATURES */
        .po-features-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
        .po-feature-card { background: #fff; border: 1px solid ${T.border}; border-radius: 4px; padding: 22px; transition: border-color 0.15s, box-shadow 0.15s; }
        .po-feature-card:hover { border-color: ${T.blue}; box-shadow: 0 4px 14px rgba(0,0,0,0.04); }
        .po-feature-card-icon { width: 44px; height: 44px; border-radius: 10px; background: ${T.blueLt}; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
        .po-feature-card-icon i { color: ${T.blue}; font-size: 18px; }
        .po-feature-card-title { font-size: 14px; font-weight: 700; color: ${T.navy}; margin: 0 0 6px 0; letter-spacing: -0.2px; }
        .po-feature-card-desc { font-size: 13px; color: ${T.muted}; line-height: 1.6; margin: 0; }

        /* USE CASES */
        .po-usecases { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
        .po-usecase { background: ${T.bg}; border: 1px solid ${T.border}; border-radius: 4px; padding: 24px; transition: background 0.15s, border-color 0.15s, transform 0.15s; }
        .po-usecase:hover { background: #fff; border-color: ${T.blue}; transform: translateY(-2px); }
        .po-usecase-icon { width: 48px; height: 48px; border-radius: 50%; background: #fff; border: 1px solid ${T.border}; display: flex; align-items: center; justify-content: center; margin-bottom: 16px; transition: background 0.15s, border-color 0.15s; }
        .po-usecase:hover .po-usecase-icon { background: ${T.blueLt}; border-color: ${T.blue}; }
        .po-usecase-icon i { color: ${T.blue}; font-size: 18px; }
        .po-usecase-title { font-size: 15px; font-weight: 700; color: ${T.navy}; margin: 0 0 8px 0; letter-spacing: -0.2px; }
        .po-usecase-desc { font-size: 13px; color: ${T.muted}; line-height: 1.6; margin: 0; }

        /* INSTANCE TYPES */
        .po-instances { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        .po-instance { position: relative; background: #fff; border: 1px solid ${T.border}; border-radius: 4px; padding: 26px 22px 22px; overflow: hidden; transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s; display: flex; flex-direction: column; }
        .po-instance:hover { border-color: ${T.blue}; box-shadow: 0 6px 18px rgba(0,0,0,0.05); transform: translateY(-2px); }
        .po-instance-icon { width: 48px; height: 48px; border-radius: 10px; background: linear-gradient(135deg, ${T.blue} 0%, ${T.navy} 100%); display: flex; align-items: center; justify-content: center; margin-bottom: 16px; }
        .po-instance-icon i { color: #fff; font-size: 20px; }
        .po-instance-name { font-size: 15px; font-weight: 700; color: ${T.navy}; margin: 0 0 4px 0; letter-spacing: -0.2px; }
        .po-instance-ratio { font-size: 10px; font-weight: 700; color: ${T.blue}; text-transform: uppercase; letter-spacing: 0.7px; margin-bottom: 14px; }
        .po-instance-desc { font-size: 13px; color: ${T.muted}; line-height: 1.6; margin: 0 0 16px 0; }
        .po-instance-cases { list-style: none; padding: 14px 0 0 0; margin: auto 0 0 0; border-top: 1px solid ${T.border}; }
        .po-instance-case { font-size: 12px; color: ${T.navy}; padding: 5px 0; display: flex; align-items: center; gap: 8px; line-height: 1.4; }
        .po-instance-case i { color: ${T.blue}; font-size: 8px; }

        /* FAQ */
        .po-faq-item { display: flex; gap: 14px; background: #fff; border: 1px solid ${T.border}; border-radius: 4px; padding: 20px 22px; margin-bottom: 10px; transition: border-color 0.15s; }
        .po-faq-item:hover { border-color: ${T.blue}; }
        .po-faq-item:last-child { margin-bottom: 0; }
        .po-faq-q-icon { width: 28px; height: 28px; border-radius: 6px; background: ${T.blueLt}; color: ${T.blue}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 12px; font-weight: 800; font-family: Georgia, serif; }
        .po-faq-body { flex: 1; }
        .po-faq-q { font-size: 14px; font-weight: 600; color: ${T.navy}; margin: 0 0 8px 0; line-height: 1.4; }
        .po-faq-a { font-size: 13px; color: ${T.muted}; line-height: 1.7; margin: 0; }

        /* CTA buttons */
        .po-cta { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 12px 22px; background: ${T.blue}; color: #fff; text-decoration: none; font-size: 13px; font-weight: 600; border-radius: 3px; transition: opacity 0.15s, transform 0.15s; }
        .po-cta:hover { opacity: 0.92; transform: translateY(-1px); }
        .po-cta-dark { background: ${T.navy}; }
        .po-cta-secondary { background: #fff; color: ${T.navy}; border: 1px solid ${T.border}; }
        .po-cta-secondary:hover { border-color: ${T.blue}; color: ${T.blue}; }

        /* BOTTOM CTA */
        .po-bottom { position: relative; background: linear-gradient(135deg, ${T.navy} 0%, ${T.blue} 100%); border-radius: 4px; padding: 48px 36px; text-align: center; overflow: hidden; color: #fff; }
        .po-bottom-decor { position: absolute; top: 0; right: 0; bottom: 0; left: 0; opacity: 0.08; pointer-events: none; }
        .po-bottom-inner { position: relative; z-index: 2; }
        .po-bottom-title { font-size: 22px; font-weight: 700; color: #fff; margin: 0 0 10px 0; letter-spacing: -0.3px; }
        .po-bottom-sub { font-size: 14px; color: rgba(255,255,255,0.85); margin: 0 auto 24px auto; max-width: 600px; line-height: 1.7; }
        .po-bottom-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
        .po-bottom-actions .po-cta { align-self: auto; background: #fff; color: ${T.navy}; }
        .po-bottom-actions .po-cta:hover { background: ${T.bg}; }
        .po-bottom-actions .po-cta-outline { background: transparent; color: #fff; border: 1px solid rgba(255,255,255,0.4); }
        .po-bottom-actions .po-cta-outline:hover { background: rgba(255,255,255,0.1); }

        /* RESPONSIVE */
        @media (max-width: 1024px) {
          .po-distros, .po-features-grid, .po-usecases { grid-template-columns: repeat(2, 1fr); }
          .po-instances { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 920px) {
          .po-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 20px; }
          .po-hero-decor { opacity: 0.4; width: 360px; right: -80px; }
        }
        @media (max-width: 768px) {
          .po-hero { padding: 40px 0 48px; }
          .po-govde { padding: 36px 0; }
          .po-section { margin-bottom: 44px; }
          .po-distros, .po-features-grid, .po-usecases, .po-instances { grid-template-columns: 1fr; gap: 14px; }
          .po-bottom { padding: 36px 24px; }
        }
        @media (max-width: 480px) {
          .po-hero { padding: 28px 0 32px; }
          .po-hero-decor { display: none; }
          .po-stats-grid { grid-template-columns: 1fr; gap: 14px; }
          .po-govde { padding: 28px 0; }
        }
      `}</style>

      {/* HERO */}
      <div className="po-hero">
        <svg className="po-hero-decor" viewBox="0 0 540 420" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          {/* Soft cloud blobs */}
          <ellipse cx="320" cy="180" rx="220" ry="150" fill={T.blue} opacity="0.05" />
          <ellipse cx="430" cy="90" rx="90" ry="60" fill={T.blue} opacity="0.06" />
          <circle cx="160" cy="80" r="36" fill={T.navy} opacity="0.05" />
          <circle cx="460" cy="320" r="60" fill={T.blue} opacity="0.06" />

          {/* Server rack stack */}
          <g transform="translate(170,140)">
            <rect x="0" y="0" width="220" height="46" rx="6" fill="#fff" stroke={T.border} strokeWidth="1.5" />
            <circle cx="20" cy="23" r="5" fill="#22c55e" />
            <rect x="40" y="18" width="80" height="4" rx="2" fill={T.muted} opacity="0.35" />
            <rect x="40" y="26" width="50" height="4" rx="2" fill={T.muted} opacity="0.25" />
            <rect x="180" y="18" width="20" height="12" rx="2" fill={T.blueLt} />

            <rect x="0" y="58" width="220" height="46" rx="6" fill="#fff" stroke={T.border} strokeWidth="1.5" />
            <circle cx="20" cy="81" r="5" fill={T.blue} />
            <rect x="40" y="76" width="100" height="4" rx="2" fill={T.muted} opacity="0.35" />
            <rect x="40" y="84" width="60" height="4" rx="2" fill={T.muted} opacity="0.25" />
            <rect x="180" y="76" width="20" height="12" rx="2" fill={T.blueLt} />

            <rect x="0" y="116" width="220" height="46" rx="6" fill="#fff" stroke={T.border} strokeWidth="1.5" />
            <circle cx="20" cy="139" r="5" fill="#f59e0b" />
            <rect x="40" y="134" width="70" height="4" rx="2" fill={T.muted} opacity="0.35" />
            <rect x="40" y="142" width="90" height="4" rx="2" fill={T.muted} opacity="0.25" />
            <rect x="180" y="134" width="20" height="12" rx="2" fill={T.blueLt} />
          </g>

          {/* Terminal window */}
          <g transform="translate(60,250)" opacity="0.95">
            <rect x="0" y="0" width="150" height="100" rx="8" fill={T.navy} />
            <circle cx="14" cy="14" r="3" fill="#ef4444" />
            <circle cx="24" cy="14" r="3" fill="#f59e0b" />
            <circle cx="34" cy="14" r="3" fill="#22c55e" />
            <rect x="12" y="34" width="6" height="3" rx="1.5" fill="#22c55e" />
            <rect x="22" y="34" width="60" height="3" rx="1.5" fill="#fff" opacity="0.85" />
            <rect x="12" y="46" width="90" height="3" rx="1.5" fill="#fff" opacity="0.55" />
            <rect x="12" y="58" width="70" height="3" rx="1.5" fill="#fff" opacity="0.55" />
            <rect x="12" y="70" width="6" height="3" rx="1.5" fill="#22c55e" />
            <rect x="22" y="70" width="3" height="9" fill="#fff" opacity="0.9" />
          </g>

          {/* Connection dotted line */}
          <path d="M210 180 Q 240 230, 200 280" stroke={T.blue} strokeWidth="1.5" strokeDasharray="3,5" fill="none" opacity="0.4" />
          <path d="M390 190 Q 430 240, 470 320" stroke={T.blue} strokeWidth="1.5" strokeDasharray="3,5" fill="none" opacity="0.3" />
        </svg>

        <div className="pl-container">
          <div className="po-hero-inner">
            <Breadcrumb label={t(`${p}.breadcrumb`)} />
            <div className="po-hero-eyebrow">
              <i className="fas fa-server" style={{ fontSize: 11 }} />
              {t(`${p}.hero_eyebrow`)}
            </div>
            <h1 style={{ fontSize: 'clamp(24px, 4.2vw, 38px)', fontWeight: 700, color: T.navy, margin: 0, letterSpacing: '-0.5px', lineHeight: 1.2 }}>
              {t(`${p}.hero_title`)}
            </h1>
            <p className="po-hero-sub">{t(`${p}.hero_subtitle`)}</p>
            <div className="po-hero-actions">
              <a href={ROUTES.signup} className="po-cta d-none">
                {t(`${p}.hero_primary_cta`)}
                <i className="fas fa-arrow-right" style={{ fontSize: 11 }} />
              </a>
              <a href={ROUTES.contact} className="po-cta po-cta-secondary">
                {t(`${p}.hero_secondary_cta`)}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* STATS BAR */}
      <div className="po-stats">
        <div className="pl-container">
          <div className="po-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="po-stat">
                <div className="po-stat-icon">
                  <i className={`fas ${STATS_ICONS[i] || 'fa-check'}`} />
                </div>
                <div>
                  <div className="po-stat-value">{s.value}</div>
                  <div className="po-stat-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="po-govde">
        <div className="pl-container">

          {/* DISTRIBUTIONS */}
          <section className="po-section">
            <div className="po-section-head">
              <h2 className="po-section-title">{t(`${p}.distros.title`)}</h2>
              <p className="po-section-sub">{t(`${p}.distros.subtitle`)}</p>
            </div>
            <div className="po-distros">
              {distros.map((d, i) => {
                const ic = DISTRO_ICONS[i] || { prefix: 'fab', icon: 'fa-linux' };
                return (
                  <div key={i} className="po-distro">
                    <div className="po-distro-head">
                      <div className="po-distro-icon">
                        <i className={`${ic.prefix} ${ic.icon}`} />
                      </div>
                      <div className="po-distro-titles">
                        <h3 className="po-distro-name">{d.name}</h3>
                        {d.badge && <span className="po-distro-badge">{d.badge}</span>}
                      </div>
                    </div>
                    <p className="po-distro-desc">{d.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* FEATURES */}
          <section className="po-section">
            <div className="po-section-head">
              <h2 className="po-section-title">{t(`${p}.features.title`)}</h2>
              <p className="po-section-sub">{t(`${p}.features.subtitle`)}</p>
            </div>
            <div className="po-features-grid">
              {features.map((f, i) => (
                <div key={i} className="po-feature-card">
                  <div className="po-feature-card-icon">
                    <i className={`fas ${FEATURE_ICONS[i] || 'fa-check'}`} />
                  </div>
                  <h3 className="po-feature-card-title">{f.title}</h3>
                  <p className="po-feature-card-desc">{f.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* USE CASES */}
          <section className="po-section">
            <div className="po-section-head">
              <h2 className="po-section-title">{t(`${p}.usecases.title`)}</h2>
              <p className="po-section-sub">{t(`${p}.usecases.subtitle`)}</p>
            </div>
            <div className="po-usecases">
              {usecases.map((u, i) => (
                <div key={i} className="po-usecase">
                  <div className="po-usecase-icon">
                    <i className={`fas ${USECASE_ICONS[i] || 'fa-check'}`} />
                  </div>
                  <h3 className="po-usecase-title">{u.title}</h3>
                  <p className="po-usecase-desc">{u.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* INSTANCE TYPES */}
          <section className="po-section">
            <div className="po-section-head">
              <h2 className="po-section-title">{t(`${p}.instances.title`)}</h2>
              <p className="po-section-sub">{t(`${p}.instances.subtitle`)}</p>
            </div>
            <div className="po-instances">
              {instance_rows.map((it, i) => (
                <div key={i} className="po-instance">
                  <div className="po-instance-icon">
                    <i className={`fas ${INSTANCE_ICONS[i] || 'fa-server'}`} />
                  </div>
                  <h3 className="po-instance-name">{it.name}</h3>
                  <div className="po-instance-ratio">{it.ratio}</div>
                  <p className="po-instance-desc">{it.description}</p>
                  {Array.isArray(it.cases) && it.cases.length > 0 && (
                    <ul className="po-instance-cases">
                      {it.cases.map((c, j) => (
                        <li key={j} className="po-instance-case">
                          <i className="fas fa-circle" />
                          {c}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="po-section">
            <div className="po-section-head">
              <h2 className="po-section-title">{t(`${p}.faq.title`)}</h2>
              <p className="po-section-sub">{t(`${p}.faq.subtitle`)}</p>
            </div>
            <div>
              {faq_items.map((item, i) => (
                <div key={i} className="po-faq-item">
                  <div className="po-faq-q-icon">Q</div>
                  <div className="po-faq-body">
                    <div className="po-faq-q">{item.q}</div>
                    <p className="po-faq-a">{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* BOTTOM CTA */}
          <section className="po-section">
            <div className="po-bottom">
              <svg className="po-bottom-decor" viewBox="0 0 800 300" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                <circle cx="80" cy="50" r="60" fill="#fff" />
                <circle cx="720" cy="240" r="80" fill="#fff" />
                <circle cx="600" cy="60" r="40" fill="#fff" />
                <circle cx="200" cy="250" r="50" fill="#fff" />
                <path d="M0 200 Q 200 140, 400 180 T 800 160" stroke="#fff" strokeWidth="2" fill="none" opacity="0.4" />
              </svg>
              <div className="po-bottom-inner">
                <h3 className="po-bottom-title">{t(`${p}.cta.title`)}</h3>
                <p className="po-bottom-sub">{t(`${p}.cta.subtitle`)}</p>
                <div className="po-bottom-actions">
                  <a href={ROUTES.signup} className="po-cta d-none">
                    {t(`${p}.cta.primary`)}
                    <i className="fas fa-arrow-right" style={{ fontSize: 11 }} />
                  </a>
                  <a href={ROUTES.contact} className="po-cta po-cta-outline">
                    {t(`${p}.cta.secondary`)}
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
    </PublicLayout>
  );
}

/* ────────────────────────────────────────────────────
   PAGE: ProductSaaSCorviaPage — /product/saas/corvia
──────────────────────────────────────────────────── */
export function ProductSaaSCorviaPage() {
  const { t } = useTranslation();
  const p = 'public.static.static-pages.product.saas.corvia';

  const stats       = t(`${p}.stats.items`,                  { returnObjects: true }) || [];
  const modules     = t(`${p}.modules.items`,                { returnObjects: true }) || [];
  const bpm_feat    = t(`${p}.bpm.features.items`,           { returnObjects: true }) || [];
  const fin_feat    = t(`${p}.finance.features.items`,       { returnObjects: true }) || [];
  const fin_integ   = t(`${p}.finance.integrations.items`,   { returnObjects: true }) || [];
  const stor_items  = t(`${p}.fileservice.storage.items`,    { returnObjects: true }) || [];
  const com_feat    = t(`${p}.commerce.features.items`,      { returnObjects: true }) || [];
  const com_modes   = t(`${p}.commerce.modes.items`,         { returnObjects: true }) || [];
  const crm_feat    = t(`${p}.crm.features.items`,           { returnObjects: true }) || [];
  const prod_feat   = t(`${p}.product.features.items`,       { returnObjects: true }) || [];
  const prod_langs  = t(`${p}.product.lang_badges`,          { returnObjects: true }) || [];
  const inv_feat    = t(`${p}.inventory.features.items`,     { returnObjects: true }) || [];
  const faq_items   = t(`${p}.faq.items`,                    { returnObjects: true }) || [];

  const STATS_ICONS  = ['fa-cubes', 'fa-credit-card', 'fa-language', 'fa-headset'];
  const MODULE_ICONS = ['fa-sitemap', 'fa-credit-card', 'fa-layer-group', 'fa-th-large', 'fa-warehouse', 'fa-user-friends', 'fa-box-open'];
  const BPM_ICONS    = ['fa-project-diagram', 'fa-user-cog', 'fa-bolt', 'fa-chart-bar'];
  const FIN_ICONS    = ['fa-tag', 'fa-shield-alt', 'fa-file-invoice-dollar'];
  const STOR_ICONS   = ['fa-database', 'fa-server', 'fa-cloud'];
  const COM_ICONS    = ['fa-store', 'fa-users', 'fa-boxes', 'fa-chart-line'];
  const CRM_ICONS    = ['fa-user-circle', 'fa-history', 'fa-funnel-dollar', 'fa-users-cog'];
  const PROD_ICONS   = ['fa-tags', 'fa-language', 'fa-images', 'fa-sitemap'];
  const INV_ICONS    = ['fa-warehouse', 'fa-barcode', 'fa-bell', 'fa-plug'];

  const MOD_COLORS = [
    { bg: '#eff6ff', icon: '#2563eb' },
    { bg: '#f0fdf4', icon: '#16a34a' },
    { bg: '#f5f3ff', icon: '#7c3aed' },
    { bg: '#fff7ed', icon: '#c2410c' },
    { bg: '#f0f9ff', icon: '#0369a1' },
    { bg: '#ecfeff', icon: '#0891b2' },
    { bg: '#fdf4ff', icon: '#9333ea' },
  ];
  const MOD_BAR = ['#3b82f6','#22c55e','#8b5cf6','#f97316','#0ea5e9','#06b6d4','#a855f7'];

  return (
    <PublicLayout title={t(`${p}.meta_title`)}>
      <style>{`
        /* ─── HERO ─────────────────────────────── */
        .cv-hero { position:relative; background:linear-gradient(135deg,${T.bg} 0%,#fff 100%); border-bottom:1px solid ${T.border}; padding:64px 0 72px; overflow:hidden; }
        .cv-hero-inner { position:relative; z-index:2; max-width:640px; }
        .cv-hero-eyebrow { display:inline-flex; align-items:center; gap:8px; padding:6px 12px; background:${T.blueLt}; color:${T.blue}; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.8px; border-radius:3px; margin-bottom:16px; }
        .cv-hero-sub { font-size:15px; color:${T.muted}; margin-top:14px; max-width:620px; line-height:1.75; }
        .cv-hero-actions { display:flex; gap:12px; flex-wrap:wrap; margin-top:28px; }
        .cv-hero-decor { position:absolute; top:0; right:-40px; bottom:0; width:520px; z-index:1; pointer-events:none; }
        .cv-hero-badges { display:flex; gap:8px; flex-wrap:wrap; margin-top:18px; }
        .cv-hero-badge { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; background:#fff; border:1px solid ${T.border}; border-radius:3px; font-size:11px; font-weight:600; color:${T.navy}; }
        .cv-hero-badge i { color:${T.blue}; font-size:10px; }

        /* ─── CTA BUTTONS ─────────────────────── */
        .cv-cta { display:inline-flex; align-items:center; justify-content:center; gap:8px; padding:12px 22px; background:${T.blue}; color:#fff; text-decoration:none; font-size:13px; font-weight:600; border-radius:3px; transition:opacity 0.15s, transform 0.15s; }
        .cv-cta:hover { opacity:0.9; transform:translateY(-1px); }
        .cv-cta-secondary { background:#fff; color:${T.navy}; border:1px solid ${T.border}; }
        .cv-cta-secondary:hover { border-color:${T.blue}; color:${T.blue}; }
        .cv-cta-outline { background:transparent; color:#fff; border:1px solid rgba(255,255,255,0.4); }
        .cv-cta-outline:hover { background:rgba(255,255,255,0.1); }

        /* ─── STATS BAR ───────────────────────── */
        .cv-stats { background:#fff; border-bottom:1px solid ${T.border}; padding:28px 0; }
        .cv-stats-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:24px; }
        .cv-stat { display:flex; align-items:center; gap:14px; }
        .cv-stat-icon { width:44px; height:44px; border-radius:8px; background:${T.blueLt}; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .cv-stat-icon i { color:${T.blue}; font-size:18px; }
        .cv-stat-value { font-size:22px; font-weight:800; color:${T.navy}; line-height:1.1; letter-spacing:-0.3px; }
        .cv-stat-label { font-size:12px; color:${T.muted}; margin-top:3px; }

        /* ─── SHARED SECTION CHROME ───────────── */
        .cv-body { padding:56px 0; }
        .cv-section { margin-bottom:72px; }
        .cv-section:last-child { margin-bottom:0; }
        .cv-section-head { max-width:700px; margin:0 0 32px; }
        .cv-section-tag { display:inline-flex; align-items:center; gap:6px; padding:4px 10px; background:${T.blueLt}; color:${T.blue}; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.7px; border-radius:3px; margin-bottom:10px; }
        .cv-section-tag--green  { background:#f0fdf4; color:#16a34a; }
        .cv-section-tag--violet { background:#f5f3ff; color:#7c3aed; }
        .cv-section-tag--orange { background:#fff7ed; color:#c2410c; }
        .cv-section-tag--sky    { background:#f0f9ff; color:#0369a1; }
        .cv-section-tag--cyan   { background:#ecfeff; color:#0891b2; }
        .cv-section-tag--purple { background:#fdf4ff; color:#9333ea; }
        .cv-section-tag--premium { background:rgba(255,255,255,0.18); color:#fff; border:1px solid rgba(255,255,255,0.25); }
        .cv-section-title { font-size:24px; font-weight:700; color:${T.navy}; margin:0 0 10px; letter-spacing:-0.3px; line-height:1.25; }
        .cv-section-sub { font-size:14px; color:${T.muted}; margin:0; line-height:1.75; }

        /* ─── MODULES OVERVIEW ────────────────── */
        .cv-modules-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
        .cv-module-card { background:#fff; border:1px solid ${T.border}; border-radius:4px; padding:22px 18px; display:flex; flex-direction:column; gap:10px; transition:border-color 0.15s, box-shadow 0.15s, transform 0.15s; position:relative; overflow:hidden; }
        .cv-module-card::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; transition:opacity 0.15s; opacity:0; }
        .cv-module-card:hover { border-color:${T.blue}; box-shadow:0 6px 18px rgba(0,0,0,0.05); transform:translateY(-2px); }
        .cv-module-card:hover::before { opacity:1; }
        ${MOD_BAR.map((c, i) => `.cv-module-card:nth-child(${i+1})::before { background:${c}; }`).join('\n        ')}
        .cv-module-icon { width:44px; height:44px; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .cv-module-icon i { font-size:19px; }
        .cv-module-name { font-size:13px; font-weight:700; color:${T.navy}; margin:0; line-height:1.35; letter-spacing:-0.1px; }
        .cv-module-tag { display:inline-block; font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:0.5px; padding:2px 7px; border-radius:3px; background:${T.bg}; color:${T.muted}; align-self:flex-start; }
        .cv-module-desc { font-size:12px; color:${T.muted}; line-height:1.6; margin:0; }

        /* ─── DETAIL SECTIONS ─────────────────── */
        .cv-detail-section { background:#fff; border:1px solid ${T.border}; border-radius:4px; padding:40px; }
        .cv-detail-layout { display:grid; grid-template-columns:1fr 1fr; gap:48px; align-items:start; }
        .cv-detail-layout--reverse { direction:rtl; }
        .cv-detail-layout--reverse > * { direction:ltr; }
        .cv-detail-desc { font-size:14px; color:${T.muted}; line-height:1.75; margin:14px 0 0; }

        /* payment integration chips */
        .cv-integ-block { margin-top:24px; }
        .cv-integ-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.7px; color:${T.muted}; margin-bottom:12px; }
        .cv-integ-chips { display:flex; flex-direction:column; gap:10px; }
        .cv-integ-chip { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
        .cv-integ-name { font-size:13px; font-weight:700; color:${T.navy}; background:${T.bg}; border:1px solid ${T.border}; padding:4px 12px; border-radius:3px; }
        .cv-integ-type { font-size:11px; color:#16a34a; background:#f0fdf4; border:1px solid #bbf7d0; padding:3px 8px; border-radius:3px; font-weight:600; }

        /* feature rows */
        .cv-feat-row { display:flex; gap:16px; padding:16px 0; border-bottom:1px solid ${T.border}; }
        .cv-feat-row:last-child { border-bottom:none; padding-bottom:0; }
        .cv-feat-icon { width:40px; height:40px; border-radius:10px; background:${T.blueLt}; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .cv-feat-icon i { color:${T.blue}; font-size:16px; }
        .cv-feat-icon--green  { background:#f0fdf4; }
        .cv-feat-icon--green i  { color:#16a34a; }
        .cv-feat-icon--cyan   { background:#ecfeff; }
        .cv-feat-icon--cyan i   { color:#0891b2; }
        .cv-feat-icon--purple { background:#fdf4ff; }
        .cv-feat-icon--purple i { color:#9333ea; }
        .cv-feat-icon--orange { background:rgba(255,255,255,0.18); }
        .cv-feat-icon--orange i { color:#fff; }
        .cv-feat-title { font-size:13px; font-weight:700; color:${T.navy}; margin:0 0 4px; }
        .cv-feat-desc  { font-size:13px; color:${T.muted}; line-height:1.6; margin:0; }

        /* ─── FILE SERVICE ────────────────────── */
        .cv-storage-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; margin-bottom:16px; }
        .cv-storage-card { background:#fff; border:1px solid ${T.border}; border-radius:4px; padding:28px 22px; transition:border-color 0.15s, box-shadow 0.15s; }
        .cv-storage-card:hover { box-shadow:0 4px 14px rgba(0,0,0,0.05); }
        .cv-storage-card:nth-child(1) { border-top:3px solid #3b82f6; }
        .cv-storage-card:nth-child(2) { border-top:3px solid #8b5cf6; }
        .cv-storage-card:nth-child(3) { border-top:3px solid #f97316; }
        .cv-storage-icon { width:48px; height:48px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:16px; }
        .cv-storage-card:nth-child(1) .cv-storage-icon { background:#eff6ff; }
        .cv-storage-card:nth-child(1) .cv-storage-icon i { color:#2563eb; font-size:20px; }
        .cv-storage-card:nth-child(2) .cv-storage-icon { background:#f5f3ff; }
        .cv-storage-card:nth-child(2) .cv-storage-icon i { color:#7c3aed; font-size:20px; }
        .cv-storage-card:nth-child(3) .cv-storage-icon { background:#fff7ed; }
        .cv-storage-card:nth-child(3) .cv-storage-icon i { color:#c2410c; font-size:20px; }
        .cv-storage-title { font-size:15px; font-weight:700; color:${T.navy}; margin:0 0 8px; letter-spacing:-0.2px; }
        .cv-storage-desc  { font-size:13px; color:${T.muted}; line-height:1.65; margin:0 0 16px; }
        .cv-storage-feats { list-style:none; padding:14px 0 0; margin:0; border-top:1px solid ${T.border}; }
        .cv-storage-feats li { display:flex; align-items:center; gap:8px; font-size:12px; color:${T.navy}; padding:5px 0; line-height:1.4; }
        .cv-storage-feats li i { color:${T.blue}; font-size:9px; flex-shrink:0; }
        .cv-transfer-note { display:flex; align-items:flex-start; gap:16px; background:${T.blueLt}; border:1px solid #bfdbfe; border-radius:4px; padding:18px 22px; }
        .cv-transfer-icon { width:38px; height:38px; border-radius:8px; background:${T.blue}; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .cv-transfer-icon i { color:#fff; font-size:16px; }
        .cv-transfer-title { font-size:13px; font-weight:700; color:${T.navy}; margin:0 0 4px; }
        .cv-transfer-desc  { font-size:13px; color:${T.muted}; line-height:1.6; margin:0; }

        /* ─── COMMERCE (PREMIUM) ──────────────── */
        .cv-commerce-section { background:linear-gradient(135deg,${T.navy} 0%,${T.blue} 100%); border-radius:4px; padding:44px 40px; color:#fff; overflow:hidden; position:relative; }
        .cv-commerce-decor { position:absolute; top:0; right:0; bottom:0; left:0; z-index:0; pointer-events:none; overflow:hidden; }
        .cv-commerce-inner { position:relative; z-index:2; }
        .cv-commerce-section .cv-section-title { color:#fff; }
        .cv-commerce-section .cv-section-sub { color:rgba(255,255,255,0.8); }
        .cv-commerce-header { margin-bottom:36px; }
        .cv-commerce-highlight { margin-top:16px; font-size:13px; color:rgba(255,255,255,0.9); background:rgba(255,255,255,0.1); border:1px solid rgba(255,255,255,0.2); border-radius:3px; padding:12px 16px; display:inline-block; max-width:640px; line-height:1.65; }
        .cv-commerce-layout { display:grid; grid-template-columns:1fr 1fr; gap:36px; align-items:start; }
        .cv-feat-row--commerce { border-bottom-color:rgba(255,255,255,0.15); }
        .cv-feat-row--commerce .cv-feat-title { color:#fff; }
        .cv-feat-row--commerce .cv-feat-desc  { color:rgba(255,255,255,0.75); }
        .cv-mode-card { background:rgba(255,255,255,0.1); border:1px solid rgba(255,255,255,0.2); border-radius:4px; padding:24px; margin-bottom:14px; transition:background 0.15s; }
        .cv-mode-card:last-child { margin-bottom:0; }
        .cv-mode-card:hover { background:rgba(255,255,255,0.16); }
        .cv-mode-card--featured { background:rgba(255,255,255,0.18); border-color:rgba(255,255,255,0.4); }
        .cv-mode-icon { width:44px; height:44px; border-radius:10px; background:rgba(255,255,255,0.18); display:flex; align-items:center; justify-content:center; margin-bottom:14px; }
        .cv-mode-icon i { color:#fff; font-size:18px; }
        .cv-mode-title { font-size:14px; font-weight:700; color:#fff; margin:0 0 8px; }
        .cv-mode-desc  { font-size:13px; color:rgba(255,255,255,0.78); line-height:1.65; margin:0; }

        /* ─── PRODUCT MODULE ──────────────────── */
        .cv-lang-badges { display:flex; gap:8px; flex-wrap:wrap; margin-top:20px; }
        .cv-lang-badge { display:inline-flex; align-items:center; gap:6px; padding:5px 12px; background:${T.bg}; border:1px solid ${T.border}; border-radius:3px; font-size:12px; font-weight:600; color:${T.navy}; }
        .cv-lang-badge i { color:#9333ea; font-size:10px; }

        /* ─── INVENTORY ───────────────────────── */
        .cv-inv-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
        .cv-inv-card { background:#fff; border:1px solid ${T.border}; border-radius:4px; padding:22px; transition:border-color 0.15s, box-shadow 0.15s; }
        .cv-inv-card:hover { border-color:${T.blue}; box-shadow:0 4px 14px rgba(0,0,0,0.04); }
        .cv-inv-icon { width:44px; height:44px; border-radius:10px; background:#f0f9ff; display:flex; align-items:center; justify-content:center; margin-bottom:14px; }
        .cv-inv-icon i { color:#0369a1; font-size:18px; }
        .cv-inv-title { font-size:13px; font-weight:700; color:${T.navy}; margin:0 0 6px; letter-spacing:-0.1px; }
        .cv-inv-desc  { font-size:13px; color:${T.muted}; line-height:1.6; margin:0; }

        /* ─── FAQ ─────────────────────────────── */
        .cv-faq-item { display:flex; gap:14px; background:#fff; border:1px solid ${T.border}; border-radius:4px; padding:20px 22px; margin-bottom:10px; transition:border-color 0.15s; }
        .cv-faq-item:hover { border-color:${T.blue}; }
        .cv-faq-item:last-child { margin-bottom:0; }
        .cv-faq-q-icon { width:28px; height:28px; border-radius:6px; background:${T.blueLt}; color:${T.blue}; display:flex; align-items:center; justify-content:center; flex-shrink:0; font-size:12px; font-weight:800; font-family:Georgia,serif; }
        .cv-faq-body { flex:1; }
        .cv-faq-q { font-size:14px; font-weight:600; color:${T.navy}; margin:0 0 8px; line-height:1.4; }
        .cv-faq-a { font-size:13px; color:${T.muted}; line-height:1.75; margin:0; }

        /* ─── BOTTOM CTA ──────────────────────── */
        .cv-bottom { position:relative; background:linear-gradient(135deg,${T.navy} 0%,${T.blue} 100%); border-radius:4px; padding:52px 40px; text-align:center; overflow:hidden; }
        .cv-bottom-decor { position:absolute; top:0; right:0; bottom:0; left:0; z-index:0; opacity:0.07; pointer-events:none; }
        .cv-bottom-inner { position:relative; z-index:2; }
        .cv-bottom-title { font-size:24px; font-weight:700; color:#fff; margin:0 0 12px; letter-spacing:-0.3px; }
        .cv-bottom-sub { font-size:14px; color:rgba(255,255,255,0.85); margin:0 auto 28px; max-width:560px; line-height:1.75; }
        .cv-bottom-actions { display:flex; gap:12px; justify-content:center; flex-wrap:wrap; }
        .cv-bottom-actions .cv-cta { background:#fff; color:${T.navy}; }
        .cv-bottom-actions .cv-cta:hover { background:${T.bg}; }

        /* ─── RESPONSIVE ──────────────────────── */
        @media (max-width:1280px) { .cv-modules-grid { grid-template-columns:repeat(4,1fr); } }
        @media (max-width:1024px) {
          .cv-modules-grid { grid-template-columns:repeat(3,1fr); }
          .cv-detail-layout { grid-template-columns:1fr; gap:32px; }
          .cv-detail-layout--reverse { direction:ltr; }
          .cv-commerce-layout { grid-template-columns:1fr; }
          .cv-inv-grid { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width:960px) {
          .cv-stats-grid { grid-template-columns:repeat(2,1fr); gap:20px; }
          .cv-hero-decor { opacity:0.4; width:360px; right:-80px; }
          .cv-modules-grid { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width:768px) {
          .cv-hero { padding:40px 0 48px; }
          .cv-body { padding:36px 0; }
          .cv-section { margin-bottom:48px; }
          .cv-storage-grid { grid-template-columns:1fr; }
          .cv-detail-section { padding:28px 20px; }
          .cv-commerce-section { padding:32px 20px; }
          .cv-bottom { padding:36px 20px; }
        }
        @media (max-width:480px) {
          .cv-hero { padding:28px 0 36px; }
          .cv-hero-decor { display:none; }
          .cv-stats-grid { grid-template-columns:1fr; }
          .cv-body { padding:28px 0; }
          .cv-modules-grid { grid-template-columns:1fr; }
          .cv-inv-grid { grid-template-columns:1fr; }
        }
      `}</style>

      {/* ── HERO ──────────────────────────────────────── */}
      <div className="cv-hero">
        <svg className="cv-hero-decor" viewBox="0 0 520 440" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <ellipse cx="300" cy="200" rx="210" ry="150" fill={T.blue} opacity="0.04" />
          <ellipse cx="420" cy="80"  rx="80"  ry="55"  fill={T.blue} opacity="0.05" />
          <circle  cx="160" cy="70"  r="34"             fill={T.navy} opacity="0.04" />
          <circle  cx="450" cy="340" r="55"             fill={T.blue} opacity="0.05" />
          {/* Platform hub */}
          <rect x="195" y="170" width="130" height="50" rx="8" fill="#fff" stroke={T.border} strokeWidth="1.5" />
          <rect x="207" y="182" width="55" height="4"  rx="2" fill={T.navy} opacity="0.6" />
          <rect x="207" y="191" width="35" height="3"  rx="2" fill={T.muted} opacity="0.3" />
          <rect x="271" y="179" width="40" height="18" rx="4" fill={T.blueLt} />
          <rect x="276" y="184" width="30" height="3"  rx="1.5" fill={T.blue} opacity="0.7" />
          <rect x="276" y="191" width="20" height="3"  rx="1.5" fill={T.blue} opacity="0.5" />
          {/* BPM */}
          <rect x="44"  y="54"  width="100" height="38" rx="5" fill="#eff6ff" stroke="#bfdbfe" strokeWidth="1.2" />
          <circle cx="60" cy="73" r="8" fill="#3b82f6" opacity="0.2"/><circle cx="60" cy="73" r="4" fill="#3b82f6"/>
          <rect x="74" y="68" width="48" height="3" rx="1.5" fill={T.navy} opacity="0.5"/>
          <rect x="74" y="75" width="32" height="3" rx="1.5" fill={T.muted} opacity="0.3"/>
          <path d="M144 73 L195 175" stroke="#3b82f6" strokeWidth="1.1" strokeDasharray="4,4" opacity="0.45"/>
          {/* Finance */}
          <rect x="368" y="50" width="104" height="38" rx="5" fill="#f0fdf4" stroke="#bbf7d0" strokeWidth="1.2" />
          <circle cx="384" cy="69" r="8" fill="#22c55e" opacity="0.2"/><circle cx="384" cy="69" r="4" fill="#22c55e"/>
          <rect x="398" y="64" width="48" height="3" rx="1.5" fill={T.navy} opacity="0.5"/>
          <rect x="398" y="71" width="36" height="3" rx="1.5" fill={T.muted} opacity="0.3"/>
          <path d="M368 69 L325 175" stroke="#22c55e" strokeWidth="1.1" strokeDasharray="4,4" opacity="0.45"/>
          {/* File */}
          <rect x="386" y="182" width="106" height="38" rx="5" fill="#f5f3ff" stroke="#ddd6fe" strokeWidth="1.2" />
          <circle cx="402" cy="201" r="8" fill="#8b5cf6" opacity="0.2"/><circle cx="402" cy="201" r="4" fill="#8b5cf6"/>
          <rect x="416" y="196" width="48" height="3" rx="1.5" fill={T.navy} opacity="0.5"/>
          <rect x="416" y="203" width="34" height="3" rx="1.5" fill={T.muted} opacity="0.3"/>
          <path d="M386 201 L325 195" stroke="#8b5cf6" strokeWidth="1.1" strokeDasharray="4,4" opacity="0.45"/>
          {/* Commerce */}
          <rect x="340" y="318" width="112" height="38" rx="5" fill="#fff7ed" stroke="#fed7aa" strokeWidth="1.2" />
          <circle cx="356" cy="337" r="8" fill="#f97316" opacity="0.2"/><circle cx="356" cy="337" r="4" fill="#f97316"/>
          <rect x="370" y="332" width="56" height="3" rx="1.5" fill={T.navy} opacity="0.5"/>
          <rect x="370" y="339" width="40" height="3" rx="1.5" fill={T.muted} opacity="0.3"/>
          <path d="M340 325 L305 220" stroke="#f97316" strokeWidth="1.1" strokeDasharray="4,4" opacity="0.45"/>
          {/* Inventory */}
          <rect x="44"  y="318" width="112" height="38" rx="5" fill="#f0f9ff" stroke="#bae6fd" strokeWidth="1.2" />
          <circle cx="60"  cy="337" r="8" fill="#0ea5e9" opacity="0.2"/><circle cx="60" cy="337" r="4" fill="#0ea5e9"/>
          <rect x="74"  y="332" width="56" height="3" rx="1.5" fill={T.navy} opacity="0.5"/>
          <rect x="74"  y="339" width="40" height="3" rx="1.5" fill={T.muted} opacity="0.3"/>
          <path d="M156 325 L205 220" stroke="#0ea5e9" strokeWidth="1.1" strokeDasharray="4,4" opacity="0.45"/>
          {/* CRM */}
          <rect x="28"  y="186" width="106" height="38" rx="5" fill="#ecfeff" stroke="#a5f3fc" strokeWidth="1.2" />
          <circle cx="44"  cy="205" r="8" fill="#06b6d4" opacity="0.2"/><circle cx="44" cy="205" r="4" fill="#06b6d4"/>
          <rect x="58"  y="200" width="52" height="3" rx="1.5" fill={T.navy} opacity="0.5"/>
          <rect x="58"  y="207" width="36" height="3" rx="1.5" fill={T.muted} opacity="0.3"/>
          <path d="M134 205 L195 192" stroke="#06b6d4" strokeWidth="1.1" strokeDasharray="4,4" opacity="0.45"/>
          {/* Product */}
          <rect x="200" y="388" width="120" height="38" rx="5" fill="#fdf4ff" stroke="#e9d5ff" strokeWidth="1.2" />
          <circle cx="216" cy="407" r="8" fill="#a855f7" opacity="0.2"/><circle cx="216" cy="407" r="4" fill="#a855f7"/>
          <rect x="230" y="402" width="60" height="3" rx="1.5" fill={T.navy} opacity="0.5"/>
          <rect x="230" y="409" width="44" height="3" rx="1.5" fill={T.muted} opacity="0.3"/>
          <path d="M260 388 L260 220" stroke="#a855f7" strokeWidth="1.1" strokeDasharray="4,4" opacity="0.45"/>
          <circle cx="260" cy="195" r="3" fill={T.blue} />
        </svg>

        <div className="pl-container">
          <div className="cv-hero-inner">
            <Breadcrumb label={t(`${p}.breadcrumb`)} />
            <div className="cv-hero-eyebrow">
              <i className="fas fa-cubes" style={{ fontSize: 11 }} />
              {t(`${p}.hero_eyebrow`)}
            </div>
            <h1 style={{ fontSize: 'clamp(24px,4.2vw,38px)', fontWeight: 700, color: T.navy, margin: 0, letterSpacing: '-0.5px', lineHeight: 1.2 }}>
              {t(`${p}.hero_title`)}
            </h1>
            <p className="cv-hero-sub">{t(`${p}.hero_subtitle`)}</p>
            <div className="cv-hero-badges">
              <span className="cv-hero-badge"><i className="fas fa-check" />{t(`${p}.hero_badge_modules`)}</span>
              <span className="cv-hero-badge"><i className="fas fa-language" />{t(`${p}.hero_badge_lang`)}</span>
              <span className="cv-hero-badge"><i className="fas fa-plug" />{t(`${p}.hero_badge_api`)}</span>
            </div>
            <div className="cv-hero-actions">
              <a href={ROUTES.signup} className="cv-cta">
                {t(`${p}.hero_primary_cta`)}
                <i className="fas fa-arrow-right" style={{ fontSize: 11 }} />
              </a>
              <a href={ROUTES.contact} className="cv-cta cv-cta-secondary">
                {t(`${p}.hero_secondary_cta`)}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS BAR ─────────────────────────────────── */}
      <div className="cv-stats">
        <div className="pl-container">
          <div className="cv-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="cv-stat">
                <div className="cv-stat-icon">
                  <i className={`fas ${STATS_ICONS[i] || 'fa-check'}`} />
                </div>
                <div>
                  <div className="cv-stat-value">{s.value}</div>
                  <div className="cv-stat-label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── BODY ──────────────────────────────────────── */}
      <div className="cv-body">
        <div className="pl-container">

          {/* MODULE OVERVIEW */}
          <section className="cv-section">
            <div className="cv-section-head">
              <h2 className="cv-section-title">{t(`${p}.modules.title`)}</h2>
              <p className="cv-section-sub">{t(`${p}.modules.subtitle`)}</p>
            </div>
            <div className="cv-modules-grid">
              {modules.map((m, i) => {
                const mc = MOD_COLORS[i] || MOD_COLORS[0];
                return (
                  <div key={i} className="cv-module-card">
                    <div className="cv-module-icon" style={{ background: mc.bg }}>
                      <i className={`fas ${MODULE_ICONS[i] || 'fa-cube'}`} style={{ color: mc.icon }} />
                    </div>
                    <h3 className="cv-module-name">{m.title}</h3>
                    {m.tag && <span className="cv-module-tag">{m.tag}</span>}
                    <p className="cv-module-desc">{m.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* BPM */}
          <section className="cv-section cv-detail-section">
            <div className="cv-detail-layout">
              <div>
                <span className="cv-section-tag">{t(`${p}.bpm.tag`)}</span>
                <h2 className="cv-section-title">{t(`${p}.bpm.title`)}</h2>
                <p className="cv-detail-desc">{t(`${p}.bpm.description`)}</p>
                <svg viewBox="0 0 320 80" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: 300, marginTop: 24 }} aria-hidden="true">
                  {[0,1,2,3].map(idx => (
                    <g key={idx} transform={`translate(${idx * 78},0)`}>
                      <rect x="2" y="16" width="68" height="48" rx="6" fill={idx === 1 ? T.blueLt : T.bg} stroke={idx === 1 ? T.blue : T.border} strokeWidth="1.2" />
                      <circle cx="20" cy="40" r="7" fill={idx === 1 ? T.blue : T.border} opacity="0.7" />
                      <rect x="33" y="36" width="28" height="3" rx="1.5" fill={T.muted} opacity="0.45" />
                      <rect x="33" y="43" width="18" height="3" rx="1.5" fill={T.muted} opacity="0.3" />
                      {idx < 3 && <path d="M72 40 L78 40" stroke={T.border} strokeWidth="1.5" />}
                    </g>
                  ))}
                </svg>
              </div>
              <div>
                {bpm_feat.map((f, i) => (
                  <div key={i} className="cv-feat-row">
                    <div className="cv-feat-icon"><i className={`fas ${BPM_ICONS[i] || 'fa-check'}`} /></div>
                    <div><div className="cv-feat-title">{f.title}</div><div className="cv-feat-desc">{f.description}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FINANCE */}
          <section className="cv-section cv-detail-section">
            <div className="cv-detail-layout cv-detail-layout--reverse">
              <div>
                <span className="cv-section-tag cv-section-tag--green">{t(`${p}.finance.tag`)}</span>
                <h2 className="cv-section-title">{t(`${p}.finance.title`)}</h2>
                <p className="cv-detail-desc">{t(`${p}.finance.description`)}</p>
                <div className="cv-integ-block">
                  <div className="cv-integ-label">{t(`${p}.finance.integrations_label`)}</div>
                  <div className="cv-integ-chips">
                    {fin_integ.map((integ, i) => (
                      <div key={i} className="cv-integ-chip">
                        <span className="cv-integ-name">{integ.name}</span>
                        {Array.isArray(integ.types) && integ.types.map((type, j) => (
                          <span key={j} className="cv-integ-type">{type}</span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                {fin_feat.map((f, i) => (
                  <div key={i} className="cv-feat-row">
                    <div className="cv-feat-icon cv-feat-icon--green"><i className={`fas ${FIN_ICONS[i] || 'fa-check'}`} /></div>
                    <div><div className="cv-feat-title">{f.title}</div><div className="cv-feat-desc">{f.description}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FILE SERVICE */}
          <section className="cv-section">
            <div className="cv-section-head">
              <span className="cv-section-tag cv-section-tag--violet">{t(`${p}.fileservice.tag`)}</span>
              <h2 className="cv-section-title">{t(`${p}.fileservice.title`)}</h2>
              <p className="cv-section-sub">{t(`${p}.fileservice.subtitle`)}</p>
            </div>
            <div className="cv-storage-grid">
              {stor_items.map((s, i) => (
                <div key={i} className="cv-storage-card">
                  <div className="cv-storage-icon"><i className={`fas ${STOR_ICONS[i] || 'fa-server'}`} /></div>
                  <h3 className="cv-storage-title">{s.title}</h3>
                  <p className="cv-storage-desc">{s.description}</p>
                  {Array.isArray(s.features) && (
                    <ul className="cv-storage-feats">
                      {s.features.map((f, j) => <li key={j}><i className="fas fa-check" />{f}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            <div className="cv-transfer-note">
              <div className="cv-transfer-icon"><i className="fas fa-exchange-alt" /></div>
              <div>
                <div className="cv-transfer-title">{t(`${p}.fileservice.auto_transfer_title`)}</div>
                <div className="cv-transfer-desc">{t(`${p}.fileservice.auto_transfer_desc`)}</div>
              </div>
            </div>
          </section>

          {/* COMMERCE — PREMIUM */}
          <section className="cv-section cv-commerce-section">
            <svg className="cv-commerce-decor" viewBox="0 0 900 500" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
              <circle cx="80"  cy="60"  r="80"  fill="#fff" opacity="0.06" />
              <circle cx="820" cy="440" r="100" fill="#fff" opacity="0.06" />
              <circle cx="750" cy="80"  r="50"  fill="#fff" opacity="0.06" />
              <circle cx="150" cy="430" r="60"  fill="#fff" opacity="0.06" />
              <path d="M0 300 Q 250 200, 450 240 T 900 220" stroke="#fff" strokeWidth="1.5" fill="none" opacity="0.15" />
            </svg>
            <div className="cv-commerce-inner">
              <div className="cv-commerce-header">
                <span className="cv-section-tag cv-section-tag--premium">{t(`${p}.commerce.tag`)}</span>
                <h2 className="cv-section-title">{t(`${p}.commerce.title`)}</h2>
                <p className="cv-section-sub">{t(`${p}.commerce.subtitle`)}</p>
                <div className="cv-commerce-highlight">
                  <i className="fas fa-star" style={{ marginRight: 8, opacity: 0.8 }} />
                  {t(`${p}.commerce.highlight`)}
                </div>
              </div>
              <div className="cv-commerce-layout">
                <div>
                  {com_feat.map((f, i) => (
                    <div key={i} className="cv-feat-row cv-feat-row--commerce">
                      <div className="cv-feat-icon cv-feat-icon--orange"><i className={`fas ${COM_ICONS[i] || 'fa-check'}`} /></div>
                      <div><div className="cv-feat-title">{f.title}</div><div className="cv-feat-desc">{f.description}</div></div>
                    </div>
                  ))}
                </div>
                <div>
                  {com_modes.map((m, i) => (
                    <div key={i} className={`cv-mode-card${i === 1 ? ' cv-mode-card--featured' : ''}`}>
                      <div className="cv-mode-icon"><i className={`fas ${i === 0 ? 'fa-store' : 'fa-users'}`} /></div>
                      <h4 className="cv-mode-title">{m.title}</h4>
                      <p className="cv-mode-desc">{m.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* CRM */}
          <section className="cv-section cv-detail-section">
            <div className="cv-detail-layout">
              <div>
                <span className="cv-section-tag cv-section-tag--cyan">{t(`${p}.crm.tag`)}</span>
                <h2 className="cv-section-title">{t(`${p}.crm.title`)}</h2>
                <p className="cv-detail-desc">{t(`${p}.crm.description`)}</p>
                {/* Mini CRM pipeline SVG */}
                <svg viewBox="0 0 300 72" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', maxWidth: 300, marginTop: 24 }} aria-hidden="true">
                  {['#06b6d4','#3b82f6','#22c55e','#f59e0b'].map((clr, idx) => (
                    <g key={idx} transform={`translate(${idx * 72},0)`}>
                      <rect x="2" y="8" width="62" height="56" rx="6" fill={T.bg} stroke={T.border} strokeWidth="1.2" />
                      <rect x="10" y="4" width="46" height="8" rx="3" fill={clr} opacity="0.85" />
                      <circle cx="18" cy="34" r="7" fill={clr} opacity="0.15" />
                      <circle cx="18" cy="34" r="3.5" fill={clr} opacity="0.7" />
                      <rect x="30" y="30" width="26" height="3" rx="1.5" fill={T.muted} opacity="0.4" />
                      <rect x="30" y="37" width="18" height="3" rx="1.5" fill={T.muted} opacity="0.25" />
                      <rect x="10" y="48" width="46" height="10" rx="3" fill={clr} opacity="0.1" />
                    </g>
                  ))}
                </svg>
              </div>
              <div>
                {crm_feat.map((f, i) => (
                  <div key={i} className="cv-feat-row">
                    <div className="cv-feat-icon cv-feat-icon--cyan"><i className={`fas ${CRM_ICONS[i] || 'fa-check'}`} /></div>
                    <div><div className="cv-feat-title">{f.title}</div><div className="cv-feat-desc">{f.description}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* PRODUCT MODULE */}
          <section className="cv-section cv-detail-section">
            <div className="cv-detail-layout cv-detail-layout--reverse">
              <div>
                <span className="cv-section-tag cv-section-tag--purple">{t(`${p}.product.tag`)}</span>
                <h2 className="cv-section-title">{t(`${p}.product.title`)}</h2>
                <p className="cv-detail-desc">{t(`${p}.product.description`)}</p>
                <div style={{ marginTop: 20 }}>
                  <div className="cv-integ-label">{t(`${p}.product.lang_label`)}</div>
                  <div className="cv-lang-badges">
                    {prod_langs.map((l, i) => (
                      <span key={i} className="cv-lang-badge">
                        <i className="fas fa-globe" />
                        {l}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                {prod_feat.map((f, i) => (
                  <div key={i} className="cv-feat-row">
                    <div className="cv-feat-icon cv-feat-icon--purple"><i className={`fas ${PROD_ICONS[i] || 'fa-check'}`} /></div>
                    <div><div className="cv-feat-title">{f.title}</div><div className="cv-feat-desc">{f.description}</div></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* INVENTORY */}
          <section className="cv-section">
            <div className="cv-section-head">
              <span className="cv-section-tag cv-section-tag--sky">{t(`${p}.inventory.tag`)}</span>
              <h2 className="cv-section-title">{t(`${p}.inventory.title`)}</h2>
              <p className="cv-section-sub">{t(`${p}.inventory.subtitle`)}</p>
            </div>
            <div className="cv-inv-grid">
              {inv_feat.map((f, i) => (
                <div key={i} className="cv-inv-card">
                  <div className="cv-inv-icon"><i className={`fas ${INV_ICONS[i] || 'fa-check'}`} /></div>
                  <h3 className="cv-inv-title">{f.title}</h3>
                  <p className="cv-inv-desc">{f.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="cv-section">
            <div className="cv-section-head">
              <h2 className="cv-section-title">{t(`${p}.faq.title`)}</h2>
              <p className="cv-section-sub">{t(`${p}.faq.subtitle`)}</p>
            </div>
            <div>
              {faq_items.map((item, i) => (
                <div key={i} className="cv-faq-item">
                  <div className="cv-faq-q-icon">Q</div>
                  <div className="cv-faq-body">
                    <div className="cv-faq-q">{item.q}</div>
                    <p className="cv-faq-a">{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* BOTTOM CTA */}
          <section className="cv-section">
            <div className="cv-bottom">
              <svg className="cv-bottom-decor" viewBox="0 0 800 260" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
                <circle cx="70"  cy="50"  r="60"  fill="#fff" />
                <circle cx="730" cy="210" r="80"  fill="#fff" />
                <circle cx="590" cy="50"  r="40"  fill="#fff" />
                <circle cx="190" cy="220" r="50"  fill="#fff" />
              </svg>
              <div className="cv-bottom-inner">
                <h3 className="cv-bottom-title">{t(`${p}.cta.title`)}</h3>
                <p className="cv-bottom-sub">{t(`${p}.cta.subtitle`)}</p>
                <div className="cv-bottom-actions">
                  <a href={ROUTES.signup} className="cv-cta">
                    {t(`${p}.cta.primary`)}
                    <i className="fas fa-arrow-right" style={{ fontSize: 11 }} />
                  </a>
                  <a href={ROUTES.contact} className="cv-cta cv-cta-outline">
                    {t(`${p}.cta.secondary`)}
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
    </PublicLayout>
  );
}